import API_BASE from "./apiConfig";
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  FaChartLine, FaTrophy, FaChild, FaCommentDots,
  FaBaseballBall, FaBullseye, FaShieldAlt, FaStar,
  FaCalendarAlt, FaVenusMars, FaSave, FaCheckCircle,
  FaExclamationCircle, FaSignOutAlt, FaMedal,
  FaUserCircle, FaEnvelope, FaPhone, FaFilter,
  FaRunning, FaMoon, FaSun,
} from "react-icons/fa";
import { useAppConfig } from "./AppContext";
import SixersLogo from "./Sixers_Logo.png";
import { useTheme, getT, glassCard } from "./ThemeContext";
import FuturisticBackground from "./FuturisticBackground";
import bg6 from "./Background images/6.jpg";
import CoachEvaluation from "./CoachEvaluation";

const BASE = API_BASE;
const EMPTY_ARRAY = [];
// gc is computed per-component via glassCard(T) from ThemeContext

const lbl = (T) => ({ display:"block", marginBottom:6, fontSize:11, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", color:T.textLabel, fontFamily:T.font });
const iw  = (T) => ({ display:"flex", alignItems:"center", border:`1.5px solid ${T.inputBdr}`, borderRadius:10, padding:"9px 13px", background:T.inputBg, backdropFilter:T.blur, WebkitBackdropFilter:T.blur, boxShadow:"inset 0 1px 0 rgba(255,255,255,0.04)", width:"100%", boxSizing:"border-box" });
const is  = (T) => ({ border:"none", outline:"none", width:"100%", background:"transparent", fontSize:13, color:T.inputText, fontFamily:T.font });

// ─── Category meta — supports all dynamic categories from DB ────────────────
const CAT_COLORS = [
  { color: "#ea580c", grad: "linear-gradient(135deg, #f97316, #ea580c)", lightBg: "rgba(234,88,12,0.10)", border: "rgba(234,88,12,0.30)" },
  { color: "#0284c7", grad: "linear-gradient(135deg, #0ea5e9, #0284c7)", lightBg: "rgba(2,132,199,0.10)", border: "rgba(2,132,199,0.30)" },
  { color: "#16a34a", grad: "linear-gradient(135deg, #22c55e, #16a34a)", lightBg: "rgba(22,163,74,0.10)", border: "rgba(22,163,74,0.30)" },
  { color: "#7c3aed", grad: "linear-gradient(135deg, #8b5cf6, #7c3aed)", lightBg: "rgba(124,58,237,0.10)", border: "rgba(124,58,237,0.30)" },
  { color: "#db2777", grad: "linear-gradient(135deg, #ec4899, #db2777)", lightBg: "rgba(219,39,119,0.10)", border: "rgba(219,39,119,0.30)" },
  { color: "#0891b2", grad: "linear-gradient(135deg, #06b6d4, #0891b2)", lightBg: "rgba(8,145,178,0.10)", border: "rgba(8,145,178,0.30)" },
  { color: "#d97706", grad: "linear-gradient(135deg, #fbbf24, #d97706)", lightBg: "rgba(217,119,6,0.10)",  border: "rgba(217,119,6,0.30)"  },
];

const FIXED_META = {
  Batting:         { icon: FaBaseballBall, ...CAT_COLORS[0] },
  Bowling:         { icon: FaBullseye,     ...CAT_COLORS[1] },
  Fielding:        { icon: FaShieldAlt,    ...CAT_COLORS[2] },
  Fitness:         { icon: FaRunning,      ...CAT_COLORS[3] },
  "Game Awareness":{ icon: FaBullseye,     ...CAT_COLORS[4] },
  "Mental Skills": { icon: FaStar,         ...CAT_COLORS[5] },
  Teamwork:        { icon: FaChild,        ...CAT_COLORS[6] },
  Wicketkeeping:   { icon: FaBaseballBall, ...CAT_COLORS[5] },
};

function getCatMeta(name, idx) {
  if (FIXED_META[name]) return FIXED_META[name];
  const c = CAT_COLORS[idx % CAT_COLORS.length];
  return { icon:FaStar, ...c };
}

// ─── Timeframes ─────────────────────────────────────────────────────────────
const TIMEFRAMES = [
  { id:"30d",    label:"30 Days", days:30   },
  { id:"60d",    label:"60 Days", days:60   },
  { id:"90d",    label:"90 Days", days:90   },
  { id:"1y",     label:"1 Year",  days:365  },
  { id:"all",    label:"All",     days:null },
  { id:"custom", label:"Custom",  days:null },
];

function filterByTimeframe(trend, tfId, customFrom, customTo) {
  if (!trend || !trend.length) return [];
  if (tfId === "all") return trend;
  const now = new Date();
  let from, to;
  if (tfId === "custom") {
    if (!customFrom) return trend;
    from = new Date(customFrom);
    to   = customTo ? new Date(customTo) : now;
  } else {
    const tf = TIMEFRAMES.find(t => t.id === tfId);
    to = now; from = new Date(now);
    from.setDate(from.getDate() - tf.days);
  }
  return trend.filter(t => { const d = new Date(t.date); return d >= from && d <= to; });
}

function useTimeframe() {
  const [tfId,       setTfId]       = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo,   setCustomTo]   = useState("");
  return { tfId, setTfId, customFrom, setCustomFrom, customTo, setCustomTo };
}

// ─── Compact timeframe pill ──────────────────────────────────────────────────
function TfPill({ tf }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:4, flexWrap:"wrap" }}>
      <div style={{ display:"flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:20,
        background:T.inputBg, border:`1px solid ${T.inputBdr}`,
        backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)" }}>
        <FaFilter size={10} style={{ color:T.textLabel }}/>
        <select value={tf.tfId} onChange={e=>tf.setTfId(e.target.value)}
          style={{ border:"none", outline:"none", background:"transparent", fontSize:10, fontWeight:700,
            color:T.inputText, fontFamily:T.font, cursor:"pointer", WebkitTapHighlightColor:"transparent", colorScheme: isDark ? "dark" : "light" }}>
          {TIMEFRAMES.map(t=><option key={t.id} value={t.id} style={{ background:T.dropdownBg, color:T.dropdownItem }}>{t.label}</option>)}
        </select>
      </div>
      {tf.tfId==="custom" && (
        <>
          <input type="date" value={tf.customFrom} onChange={e=>tf.setCustomFrom(e.target.value)}
            style={{ fontSize:10, padding:"4px 8px", borderRadius:8, border:`1px solid ${T.inputBdr}`,
              fontFamily:T.font, color:T.inputText, background:T.inputBg, width:106 }}/>
          <span style={{ fontSize:9, color:T.textMuted }}>→</span>
          <input type="date" value={tf.customTo} onChange={e=>tf.setCustomTo(e.target.value)}
            style={{ fontSize:10, padding:"4px 8px", borderRadius:8, border:`1px solid ${T.inputBdr}`,
              fontFamily:T.font, color:T.inputText, background:T.inputBg, width:106 }}/>
        </>
      )}
    </div>
  );
}

// ─── Section divider ────────────────────────────────────────────────────────
function SectionLabel({ children, right }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
      <div style={{ height:14, width:3, background:T.yellow, borderRadius:2, flexShrink:0,
        boxShadow:`0 0 8px ${T.yellow}80` }}/>
      <span style={{ fontSize:14, fontWeight:800, color:T.textPrimary, fontFamily:T.font, whiteSpace:"nowrap" }}>{children}</span>
      <div style={{ flex:1, height:1, background:T.divider }}/>
      {right && <div style={{ flexShrink:0 }}>{right}</div>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// NeonArc gauge — circular score dial (futuristic)
// ══════════════════════════════════════════════════════════════════════════════
function NeonArc({ value, max=10, size=130, color="#f7ce0b", label="Overall" }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const pct   = Math.min(1, Math.max(0, (isNaN(value) ? 0 : value) / max));
  const R     = (size - 18) / 2;
  const cx    = size / 2;
  const cy    = size / 2;
  const arc   = (startDeg, endDeg) => {
    const s  = startDeg * Math.PI / 180;
    const e  = endDeg   * Math.PI / 180;
    const x1 = cx + R * Math.cos(s), y1 = cy + R * Math.sin(s);
    const x2 = cx + R * Math.cos(e), y2 = cy + R * Math.sin(e);
    const lg = (endDeg - startDeg + 360) % 360 > 180 ? 1 : 0;
    return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${R} ${R} 0 ${lg} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
  };
  const trackPath = arc(-225, 45);
  const fillEnd   = -225 + pct * 270;
  const fillPath  = pct > 0.01 ? arc(-225, fillEnd) : null;
  const grade     = value >= 9 ? "ELITE" : value >= 7.5 ? "EXCELLENT" : value >= 6 ? "GOOD" : value >= 4 ? "BUILDING" : "DEVELOPING";
  const id        = `arc-${color.replace("#","")}`;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow:"visible" }}>
      <defs>
        <filter id={`${id}-glow`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <linearGradient id={`${id}-grad`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.6"/>
          <stop offset="100%" stopColor={color} stopOpacity="1"/>
        </linearGradient>
      </defs>
      {/* Track */}
      <path d={trackPath} fill="none" stroke="rgba(0,53,128,0.12)" strokeWidth={10} strokeLinecap="round"/>
      {/* Fill with glow */}
      {fillPath && (
        <path d={fillPath} fill="none" stroke={`url(#${id}-grad)`} strokeWidth={10}
          strokeLinecap="round" filter={`url(#${id}-glow)`}/>
      )}
      {/* Tick at end of fill */}
      {fillPath && pct > 0.02 && (
        <circle
          cx={(cx + R * Math.cos(fillEnd * Math.PI / 180)).toFixed(2)}
          cy={(cy + R * Math.sin(fillEnd * Math.PI / 180)).toFixed(2)}
          r={5} fill={color} filter={`url(#${id}-glow)`}/>
      )}
      {/* Value */}
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize={30} fontWeight={900}
        fill={color} fontFamily={T.font}>{isNaN(value) ? "—" : value.toFixed(1)}</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize={8.5} fontWeight={700}
        fill={T.gray} fontFamily={T.font} letterSpacing="0.09em">{grade}</text>
      <text x={cx} y={cy + 24} textAnchor="middle" fontSize={8}
        fill={T.gray} fontFamily={T.font}>{label}</text>
    </svg>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Triangle radar — futuristic dark theme
// ══════════════════════════════════════════════════════════════════════════════
// KPI Triangle — futuristic dark radar with neon glow
// ══════════════════════════════════════════════════════════════════════════════
function TriangleRadar({ data, size=300 }) {
  const { isDark } = useTheme();
  const T = getT(isDark);

  if (!data || data.length < 3) return (
    <div style={{ padding:20, textAlign:"center", color: T.chartText, fontSize:12, fontFamily:T.font,
      background: T.chartBg, border: T.chartBdr, borderRadius:14, minHeight:200,
      display:"flex", alignItems:"center", justifyContent:"center" }}>
      Scores needed in all 3 categories.
    </div>
  );
  // Padded viewBox: extra 56px top, 32px bottom, 28px each side — so labels never clip
  const vPad = { top:56, bot:40, side:32 };
  const VW = size + vPad.side * 2;
  const VH = size + vPad.top + vPad.bot;
  const cx = VW / 2;
  const cy = vPad.top + size / 2 + 8;
  const r  = size * 0.30;
  const labelR = r + 52;
  const angles = [-90, 30, 150];
  const pts  = angles.map(a => ({ x:cx+r*Math.cos(a*Math.PI/180), y:cy+r*Math.sin(a*Math.PI/180) }));
  const sPts = data.map((d,i) => ({ x:cx+r*(Math.max(0,d.value)/10)*Math.cos(angles[i]*Math.PI/180), y:cy+r*(Math.max(0,d.value)/10)*Math.sin(angles[i]*Math.PI/180) }));
  const grids = [0.25,0.5,0.75,1.0];
  return (
    <div style={{ 
      background: T.chartBg,
      borderRadius:16,
      border: T.chartBdr,
      /* NO overflow:hidden — labels must not be clipped */
      display:"flex", alignItems:"center", justifyContent:"center",
      boxShadow: isDark ? "0 4px 32px rgba(0,0,0,0.50), inset 0 1px 0 rgba(255,255,255,0.04)" : "0 4px 32px rgba(3,78,123,0.08), inset 0 1px 0 #fff",
      padding:"4px 0" }}>
      <svg width="100%" viewBox={`0 0 ${VW} ${VH}`} style={{ overflow:"visible", display:"block" }}>
        <defs>
          <filter id="radar-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <filter id="radar-dot-glow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <radialGradient id="radar-fill" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor={T.yellow} stopOpacity="0.24"/>
            <stop offset="100%" stopColor={T.yellow} stopOpacity="0.06"/>
          </radialGradient>
        </defs>

        {/* Grid polygons */}
        {grids.map((lv,li)=>(
          <polygon key={li}
            points={angles.map(a=>`${cx+r*lv*Math.cos(a*Math.PI/180)},${cy+r*lv*Math.sin(a*Math.PI/180)}`).join(" ")}
            fill={li===3?(isDark ? "rgba(255,255,255,0.025)" : "rgba(3,78,123,0.03)"):"none"}
            stroke={li===3 ? (isDark ? "rgba(255,255,255,0.15)" : "rgba(3,78,123,0.18)") : T.chartGrid}
            strokeWidth={li===3?1.4:0.7}/>
        ))}

        {/* Axis spokes */}
        {pts.map((p,i)=>(
          <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y}
            stroke={T.chartGrid} strokeWidth={1}/>
        ))}

        {/* Filled polygon with glow */}
        <polygon points={sPts.map(p=>`${p.x},${p.y}`).join(" ")}
          fill="url(#radar-fill)" stroke={T.yellow} strokeWidth={2}
          strokeLinejoin="round" filter="url(#radar-glow)" opacity={0.92}/>

        {/* Vertex dots */}
        {sPts.map((p,i)=>{
          const m = FIXED_META[data[i].label] || { color:T.yellow };
          return (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r={12} fill={m.color} opacity={0.10}/>
              <circle cx={p.x} cy={p.y} r={6}  fill={m.color} stroke="rgba(255,255,255,0.55)" strokeWidth={1.5}
                filter="url(#radar-dot-glow)"/>
            </g>
          );
        })}

        {/* Labels — with padded viewBox, never clipped */}
        {pts.map((p,i)=>{
          const lx = cx + labelR * Math.cos(angles[i]*Math.PI/180);
          const ly = cy + labelR * Math.sin(angles[i]*Math.PI/180);
          const m  = FIXED_META[data[i].label] || { color:T.yellow };
          return (
            <g key={i}>
              <text x={lx} y={ly-14} textAnchor="middle" fontSize={11} fontWeight="800"
                fill={T.chartText} fontFamily={T.font} letterSpacing="0.07em">
                {data[i].label.toUpperCase()}
              </text>
              <text x={lx} y={ly+16} textAnchor="middle" fontSize={28} fontWeight="900"
                fill={m.color} fontFamily={T.font} filter="url(#radar-dot-glow)">
                {data[i].value.toFixed(1)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Compact mini sparkline for category cards
// ══════════════════════════════════════════════════════════════════════════════
function MiniSparkline({ trend, catKey }) {
  const pts = useMemo(()=>trend.filter(t=>t[catKey]!==undefined).map(t=>t[catKey]||0),[trend,catKey]);
  if (pts.length < 2) return <div style={{ height:28 }}/>;
  const W=120, H=28, pad=2;
  const iW=W-pad*2, iH=H-pad*2;
  const min=Math.min(...pts), max=Math.max(...pts)||1, rng=max-min||1;
  const xOf=(i)=>pad+i*(iW/(pts.length-1));
  const yOf=(v)=>pad+iH-((v-min)/rng)*iH;
  // Use ONE consistent color for all graphs as requested (using Brand Blue)
  const graphColor = "#034e7b"; 
  let d=`M ${xOf(0).toFixed(1)} ${yOf(pts[0]).toFixed(1)}`;
  for(let i=1;i<pts.length;i++){
    const cx1=((xOf(i-1)+xOf(i))/2).toFixed(1);
    d+=` C ${cx1} ${yOf(pts[i-1]).toFixed(1)}, ${cx1} ${yOf(pts[i]).toFixed(1)}, ${xOf(i).toFixed(1)} ${yOf(pts[i]).toFixed(1)}`;
  }
  return (
    <svg width={W} height={H} style={{ overflow:"visible" }}>
      <path d={d} fill="none" stroke={graphColor} strokeWidth={1.8} strokeLinecap="round"/>
      <circle cx={xOf(pts.length-1)} cy={yOf(pts[pts.length-1])} r={2.5} fill={graphColor}/>
    </svg>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Trend chart — futuristic dark: glow lines, area fills, hover tooltip
// ══════════════════════════════════════════════════════════════════════════════
function TrendChart({ trend, categories, chartId="main" }) {
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  // Smart time-evenly-spaced ticks — prevents clustering / label overlap
  const ticks = useMemo(() => {
    if (!trend || !trend.length) return [];
    if (trend.length <= 4) return trend.map(t => t.date);
    const ts = trend.map(t => new Date(t.date).getTime());
    const tMin = Math.min(...ts), tRng = Math.max(...ts) - tMin || 1;
    // 5 evenly-spaced in time (incl. first + last)
    const want = 5;
    const picked = new Set([trend[0].date, trend[trend.length - 1].date]);
    for (let k = 1; k < want - 1; k++) {
      const target = tMin + (k / (want - 1)) * tRng;
      let best = trend[0].date, bestD = Infinity;
      trend.forEach(t => { const d = Math.abs(new Date(t.date).getTime() - target); if (d < bestD) { bestD = d; best = t.date; } });
      picked.add(best);
    }
    // Sort chronologically then pixel-gap-filter (min 46 px gap, iW≈514)
    const sorted = [...picked].sort((a, b) => new Date(a) - new Date(b));
    const iW_ = 514;
    const px = d => ((new Date(d).getTime() - tMin) / tRng) * iW_;
    const minGap = 46;
    const out = [sorted[0]]; let lastPx = px(sorted[0]);
    for (let i = 1; i < sorted.length - 1; i++) {
      if (px(sorted[i]) - lastPx >= minGap) { out.push(sorted[i]); lastPx = px(sorted[i]); }
    }
    const lastD = sorted[sorted.length - 1];
    if (px(lastD) - lastPx >= minGap) out.push(lastD);
    else out[out.length - 1] = lastD;
    return out;
  }, [trend]);

  // Must be before early return to satisfy rules-of-hooks
  const handleMouseMove = useCallback((e) => {
    if (!svgRef.current || !trend || !trend.length) return;
    const _W=580, _pad={t:20,r:24,b:46,l:42};
    const _iW=_W-_pad.l-_pad.r;
    const ts=trend.map(t=>new Date(t.date).getTime());
    const _minT=Math.min(...ts), _rng=Math.max(...ts)-_minT||1;
    const _xOf=t=>_pad.l+((new Date(t.date).getTime()-_minT)/_rng)*_iW;
    const rect=svgRef.current.getBoundingClientRect();
    const svgX=(e.clientX-rect.left)*(_W/rect.width);
    let closestIdx=0, closestDist=Infinity;
    trend.forEach((t,i)=>{const d=Math.abs(_xOf(t)-svgX);if(d<closestDist){closestDist=d;closestIdx=i;}});
    const t=trend[closestIdx];
    setHover({idx:closestIdx,x:_xOf(t),date:t.date,
      values:categories.reduce((acc,cat)=>{acc[cat]=t[cat];return acc;},{})});
  }, [trend, categories]);

  const { isDark } = useTheme();
  const T = getT(isDark);

  if (!trend || trend.length === 0) return (
    <div style={{ padding:"36px 16px", textAlign:"center", color: T.chartText, fontSize:12, fontFamily:T.font, display:"flex", flexDirection:"column", alignItems:"center", gap:8,
      background: T.chartBg, border: T.chartBdr, borderRadius:14 }}>
      <FaChartLine size={24} style={{ opacity:0.15 }}/>
      No data in this period.
    </div>
  );

  const W=580, H=220;
  const pad = { t:20, r:24, b:46, l:42 };
  const iW  = W - pad.l - pad.r;
  const iH  = H - pad.t - pad.b;

  const timestamps = trend.map(t => new Date(t.date).getTime());
  const minT = Math.min(...timestamps);
  const maxT_ts = Math.max(...timestamps);
  const rng  = maxT_ts - minT || 1;
  const xOf  = t => pad.l + ((new Date(t.date).getTime() - minT) / rng) * iW;
  const yOf  = v => pad.t + iH - (Math.max(0, Math.min(10, v)) / 10) * iH;

  const fmtDate = ds => {
    const d = new Date(ds);
    const mn = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${mn[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
  };

  const bezierPath = cat => {
    const ps = trend.map(t => ({ x:xOf(t), y:yOf(t[cat] || 0) }));
    if (ps.length < 2) return null;
    let d = `M ${ps[0].x.toFixed(1)} ${ps[0].y.toFixed(1)}`;
    for (let i = 1; i < ps.length; i++) {
      const cp = ((ps[i-1].x + ps[i].x) / 2).toFixed(1);
      d += ` C ${cp} ${ps[i-1].y.toFixed(1)}, ${cp} ${ps[i].y.toFixed(1)}, ${ps[i].x.toFixed(1)} ${ps[i].y.toFixed(1)}`;
    }
    return d;
  };

  const areaPath = cat => {
    const ps = trend.map(t => ({ x:xOf(t), y:yOf(t[cat] || 0) }));
    if (ps.length < 2) return null;
    const base = pad.t + iH;
    let d = `M ${ps[0].x.toFixed(1)} ${base} L ${ps[0].x.toFixed(1)} ${ps[0].y.toFixed(1)}`;
    for (let i = 1; i < ps.length; i++) {
      const cp = ((ps[i-1].x + ps[i].x) / 2).toFixed(1);
      d += ` C ${cp} ${ps[i-1].y.toFixed(1)}, ${cp} ${ps[i].y.toFixed(1)}, ${ps[i].x.toFixed(1)} ${ps[i].y.toFixed(1)}`;
    }
    d += ` L ${ps[ps.length-1].x.toFixed(1)} ${base} Z`;
    return d;
  };

  return (
    <div style={{ position:"relative", borderRadius:16, overflow:"hidden",
      background: T.chartBg,
      border: T.chartBdr,
      boxShadow: isDark ? "0 4px 32px rgba(0,0,0,0.50), inset 0 1px 0 rgba(255,255,255,0.04)" : "0 4px 32px rgba(3,78,123,0.08), inset 0 1px 0 #fff" }}>
      <svg ref={svgRef} width="100%" viewBox={`0 0 ${W} ${H}`}
        style={{ overflow:"visible", display:"block", cursor:"crosshair" }}
        onMouseMove={handleMouseMove} onMouseLeave={() => setHover(null)}>
        <defs>
          {/* Area gradients — rich neon to transparent */}
          {categories.map(cat => {
            const m = FIXED_META[cat] || { color:T.gray };
            return (
              <linearGradient key={cat} id={`${chartId}-area-${cat}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={m.color} stopOpacity="0.40"/>
                <stop offset="55%"  stopColor={m.color} stopOpacity="0.10"/>
                <stop offset="100%" stopColor={m.color} stopOpacity="0"/>
              </linearGradient>
            );
          })}
          {/* Glow filter */}
          <filter id={`${chartId}-glow`} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <clipPath id={`${chartId}-clip`}>
            <rect x={pad.l} y={pad.t} width={iW} height={iH}/>
          </clipPath>
        </defs>

        {/* Subtle grid lines */}
        {[2, 4, 6, 8, 10].map(v => (
          <g key={v}>
            <line x1={pad.l} x2={pad.l+iW} y1={yOf(v)} y2={yOf(v)}
              stroke={T.chartGrid} strokeWidth={1}/>
            <text x={pad.l-8} y={yOf(v)+4} textAnchor="end"
              fontSize={9} fill={T.chartText} fontFamily={T.font} fontWeight={600}>{v}</text>
          </g>
        ))}

        {/* Y-axis rule */}
        <line x1={pad.l} x2={pad.l} y1={pad.t} y2={pad.t+iH}
          stroke={T.chartGrid} strokeWidth={1}/>

        {/* Hover crosshair */}
        {hover && (
          <line x1={hover.x} x2={hover.x} y1={pad.t} y2={pad.t+iH}
            stroke={isDark ? "rgba(255,255,255,0.14)" : "rgba(3,78,123,0.15)"} strokeWidth={1} strokeDasharray="4,3"/>
        )}

        {/* Clipped chart content */}
        <g clipPath={`url(#${chartId}-clip)`}>
          {/* Area fills */}
          {categories.map(cat => {
            const ap = areaPath(cat);
            return ap ? <path key={`a-${cat}`} d={ap} fill={`url(#${chartId}-area-${cat})`}/> : null;
          })}

          {/* Glow layer (soft, low-opacity) */}
          {categories.map(cat => {
            const m  = FIXED_META[cat] || { color:T.gray };
            const bp = bezierPath(cat);
            return bp ? (
              <path key={`gl-${cat}`} d={bp} fill="none" stroke={m.color}
                strokeWidth={4} strokeLinejoin="round" strokeLinecap="round" opacity={0.14}/>
            ) : null;
          })}

          {/* Main lines — thin & crisp */}
          {categories.map(cat => {
            const m  = FIXED_META[cat] || { color:T.gray };
            const bp = bezierPath(cat);
            return bp ? (
              <path key={`l-${cat}`} d={bp} fill="none" stroke={m.color}
                strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round"/>
            ) : null;
          })}

          {/* Hover dots — glowing rings */}
          {hover && categories.map(cat => {
            const m = FIXED_META[cat] || { color:T.gray };
            const t = trend[hover.idx];
            if (t[cat] === undefined) return null;
            const cx = xOf(t), cy = yOf(t[cat]);
            return (
              <g key={`hdot-${cat}`}>
                <circle cx={cx} cy={cy} r={14} fill={m.color} opacity={0.07}/>
                <circle cx={cx} cy={cy} r={7}  fill={m.color} opacity={0.18}/>
                <circle cx={cx} cy={cy} r={3.5} fill={m.color} stroke="#fff" strokeWidth={1.5}
                  filter={`url(#${chartId}-glow)`}/>
              </g>
            );
          })}

          {/* Terminal dot — only when not hovering */}
          {!hover && categories.map(cat => {
            const m   = FIXED_META[cat] || { color:T.gray };
            const pts = trend.filter(t => t[cat] !== undefined);
            if (!pts.length) return null;
            const last = pts[pts.length - 1];
            return (
              <g key={`dot-${cat}`}>
                <circle cx={xOf(last)} cy={yOf(last[cat]||0)} r={8} fill={m.color} opacity={0.12}/>
                <circle cx={xOf(last)} cy={yOf(last[cat]||0)} r={3.5} fill={m.color} stroke="#fff" strokeWidth={1.5}/>
              </g>
            );
          })}
        </g>

        {/* X-axis baseline */}
        <line x1={pad.l} x2={pad.l+iW} y1={pad.t+iH} y2={pad.t+iH}
          stroke={T.chartGrid} strokeWidth={1}/>

        {/* X-axis labels */}
        {ticks.map((ds, i) => {
          const x = pad.l + ((new Date(ds).getTime() - minT) / rng) * iW;
          return (
            <text key={i} x={x} y={pad.t+iH+16} textAnchor="middle"
              fontSize={9} fill={T.chartText} fontFamily={T.font} fontWeight={500}>
              {fmtDate(ds)}
            </text>
          );
        })}

        {/* Legend row */}
        {categories.map((cat, i) => {
          const m = FIXED_META[cat] || { color:T.gray };
          const x = pad.l + i * 140;
          const y = H - 5;
          return (
            <g key={`leg-${cat}`}>
              <line x1={x} x2={x+20} y1={y} y2={y}
                stroke={m.color} strokeWidth={2.5} strokeLinecap="round"/>
              <circle cx={x+10} cy={y} r={3} fill={m.color}
                filter={`url(#${chartId}-glow)`}/>
              <text x={x+28} y={y+4} fontSize={10}
                fill={isDark ? "rgba(255,255,255,0.65)" : "#02304f"} fontFamily={T.font} fontWeight={700}>{cat}</text>
            </g>
          );
        })}

        {/* Glassmorphism hover tooltip */}
        {hover && (() => {
          const tipW = 148, tipH = 22 + categories.length * 24 + 8;
          let tipX = hover.x + 14;
          if (tipX + tipW > W - 8) tipX = hover.x - tipW - 14;
          const tipY = pad.t + 8;
          return (
            <foreignObject x={tipX} y={tipY} width={tipW} height={tipH} style={{ overflow:"visible" }}>
              <div xmlns="http://www.w3.org/1999/xhtml" style={{
                background:"rgba(4,12,32,0.88)",
                backdropFilter:"blur(18px)",
                WebkitBackdropFilter:"blur(18px)",
                border:"1px solid rgba(255,255,255,0.10)",
                borderRadius:10,
                padding:"8px 12px",
                fontFamily:T.font,
                boxShadow:"0 8px 32px rgba(0,0,0,0.70), inset 0 1px 0 rgba(255,255,255,0.06)",
                width:"100%",
                boxSizing:"border-box",
              }}>
                <div style={{ fontSize:9,color:"rgba(255,255,255,0.40)",fontWeight:600,marginBottom:7,letterSpacing:"0.07em",textTransform:"uppercase" }}>
                  {fmtDate(hover.date)}
                </div>
                {categories.map(cat => {
                  const m = FIXED_META[cat] || { color:T.gray };
                  const v = hover.values[cat];
                  return (
                    <div key={cat} style={{ display:"flex",alignItems:"center",gap:6,marginBottom:5 }}>
                      <div style={{ width:7,height:7,borderRadius:"50%",background:m.color,flexShrink:0,
                        boxShadow:`0 0 8px ${m.color}` }}/>
                      <span style={{ fontSize:10,color:"rgba(255,255,255,0.60)",flex:1 }}>{cat}</span>
                      <span style={{ fontSize:12,fontWeight:900,color:m.color }}>
                        {v !== undefined ? parseFloat(v).toFixed(1) : "—"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </foreignObject>
          );
        })()}
      </svg>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Compact category bar chart — redesigned: clean, modern, professional
// ══════════════════════════════════════════════════════════════════════════════
function CategoryBarChart({ trend, category, chartId }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const color = "#034e7b"; // Consistent brand color for all graphs

  if (!trend || trend.length === 0)
    return <div style={{ height:140, display:"flex", alignItems:"center", justifyContent:"center", color:T.gray, fontSize:12, fontFamily:T.font }}>No data</div>;
  const sessions = trend.map(t => ({ date:t.date, value:t[category]||0 })).filter(s => s.value > 0);
  if (!sessions.length)
    return <div style={{ height:140, display:"flex", alignItems:"center", justifyContent:"center", color:T.gray, fontSize:12, fontFamily:T.font }}>No data</div>;

  const W=360, H=150;
  const pad = { t:14, r:14, b:30, l:34 };
  const iW  = W - pad.l - pad.r;
  const iH  = H - pad.t - pad.b;
  const n   = sessions.length;

  // Wider bars — cap so they don't touch when few sessions
  const slotW = iW / n;
  const barW  = Math.max(8, Math.min(28, slotW * 0.68));
  const xOf   = i => pad.l + slotW * i + slotW / 2;
  const yOf   = v => pad.t + iH - (Math.max(0, Math.min(10, v)) / 10) * iH;
  const fmtDate = ds => {
    const d = new Date(ds);
    const mn = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${mn[d.getMonth()]} ${d.getDate()}`;
  };

  // Max 4 x-axis labels, evenly spaced
  const maxXLabels = 4;
  const tickIdxs = [];
  if (n <= maxXLabels) { for (let i=0;i<n;i++) tickIdxs.push(i); }
  else { const step=(n-1)/(maxXLabels-1); for(let i=0;i<maxXLabels;i++) tickIdxs.push(Math.round(i*step)); }

  // Parse hex color for rgba usage
  const hexParts = color.replace("#","");
  const r = parseInt(hexParts.slice(0,2),16);
  const g = parseInt(hexParts.slice(2,4),16);
  const b = parseInt(hexParts.slice(4,6),16);
  const rgba = (a) => `rgba(${r},${g},${b},${a})`;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow:"visible", display:"block" }}>
      <defs>
        {/* Bar fill gradient — strong at top, fades to 12% */}
        <linearGradient id={`${chartId}-fill`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.82"/>
          <stop offset="100%" stopColor={color} stopOpacity="0.12"/>
        </linearGradient>
        {/* Subtle chart area background */}
        <linearGradient id={`${chartId}-bg`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={rgba(0.06)}/>
          <stop offset="100%" stopColor={rgba(0.02)}/>
        </linearGradient>
        <clipPath id={`${chartId}-clip`}>
          <rect x={pad.l} y={pad.t} width={iW} height={iH}/>
        </clipPath>
      </defs>

      {/* Chart area background panel */}
      <rect x={pad.l} y={pad.t} width={iW} height={iH} rx={6}
        fill={`url(#${chartId}-bg)`}/>

      {/* Horizontal grid — 0, 5, 10 */}
      {[0, 5, 10].map(v => (
        <g key={v}>
          <line x1={pad.l} x2={pad.l+iW} y1={yOf(v)} y2={yOf(v)}
            stroke={v===0 ? (isDark ? "rgba(255,255,255,0.20)" : "rgba(3,78,123,0.25)") : T.chartGrid}
            strokeWidth={v===0 ? 1.2 : 0.8}
            strokeDasharray={v===0 ? "none" : "5,4"}/>
          <text x={pad.l-6} y={yOf(v)+4} textAnchor="end"
            fontSize={10} fill={T.chartText} fontFamily={T.font} fontWeight={600}>{v}</text>
        </g>
      ))}

      {/* Axis rules */}
      <line x1={pad.l} x2={pad.l}      y1={pad.t}    y2={pad.t+iH} stroke={T.chartGrid} strokeWidth={1.2}/>
      <line x1={pad.l} x2={pad.l+iW}   y1={pad.t+iH} y2={pad.t+iH} stroke={T.chartGrid} strokeWidth={1.2}/>

      {/* Bars — clipped */}
      <g clipPath={`url(#${chartId}-clip)`}>
        {sessions.map((s, i) => {
          const x = xOf(i);
          const y = yOf(s.value);
          const h = Math.max(2, pad.t + iH - y);
          const rx = 4; // Rounded corners for premium look
          return (
            <g key={i}>
              {/* Shadow behind bar for depth */}
              <rect x={x - barW/2 + 1} y={y + 1} width={barW} height={h}
                rx={rx} fill={isDark ? "rgba(0,0,0,0.20)" : "rgba(3,78,123,0.06)"}/>
              {/* Bar body with rich gradient */}
              <rect x={x - barW/2} y={y} width={barW} height={h}
                rx={rx} fill={`url(#${chartId}-fill)`}/>
              {/* Bold top highlight stripe */}
              <rect x={x - barW/2} y={y} width={barW} height={4}
                rx={2} fill={color} opacity={0.9} filter="brightness(1.2)"/>
            </g>
          );
        })}
      </g>

      {/* X-axis labels */}
      {tickIdxs.map((ti, i) => {
        const x = xOf(ti);
        return (
          <g key={i}>
            <line x1={x} x2={x} y1={pad.t+iH} y2={pad.t+iH+4}
              stroke={T.chartGrid} strokeWidth={1}/>
            <text x={x} y={H - 4} textAnchor="middle"
              fontSize={9.5} fill={T.chartText} fontFamily={T.font} fontWeight={500}>
              {fmtDate(sessions[ti].date)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Yo-Yo bar chart (compact)
// ══════════════════════════════════════════════════════════════════════════════
// Yo-Yo Endurance — futuristic dark: neon bars, glowing trend line, tooltip
// ══════════════════════════════════════════════════════════════════════════════
function YoYoChart({ trend }) {
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  // Smart time-evenly-spaced ticks — prevents label overlap
  const ticks=useMemo(()=>{
    if(!trend||!trend.length) return [];
    if(trend.length<=4) return trend.map(t=>t.date);
    const ts=trend.map(t=>new Date(t.date).getTime());
    const tMin=Math.min(...ts), tRng=Math.max(...ts)-tMin||1;
    const want=5;
    const picked=new Set([trend[0].date, trend[trend.length-1].date]);
    for(let k=1;k<want-1;k++){
      const target=tMin+(k/(want-1))*tRng;
      let best=trend[0].date, bestD=Infinity;
      trend.forEach(t=>{const d=Math.abs(new Date(t.date).getTime()-target);if(d<bestD){bestD=d;best=t.date;}});
      picked.add(best);
    }
    const sorted=[...picked].sort((a,b)=>new Date(a)-new Date(b));
    // pixel-gap filter (iW≈434)
    const iW_=434;
    const px=d=>((new Date(d).getTime()-tMin)/tRng)*iW_;
    const minGap=48;
    const out=[sorted[0]]; let lastPx=px(sorted[0]);
    for(let i=1;i<sorted.length-1;i++){if(px(sorted[i])-lastPx>=minGap){out.push(sorted[i]);lastPx=px(sorted[i]);}}
    const lastD=sorted[sorted.length-1];
    if(px(lastD)-lastPx>=minGap) out.push(lastD); else out[out.length-1]=lastD;
    return out;
  },[trend]);

  // Must be before early return to satisfy rules-of-hooks
  const handleMouseMove=useCallback((e)=>{
    if(!svgRef.current||!trend||!trend.length) return;
    const _W=560,_pad={t:16,r:16,b:44,l:44};
    const _iW=_W-_pad.l-_pad.r,_iH=_W-_pad.t-_pad.b;
    const sess=trend.map(t=>{
      const vals=["Batting","Bowling","Fielding"].map(c=>t[c]).filter(v=>v!==undefined);
      const avg=vals.length?vals.reduce((a,b)=>a+b,0)/vals.length:0;
      return {date:t.date,level:parseFloat((5+avg*1.6).toFixed(1))};
    });
    const maxLev=Math.max(...sess.map(s=>s.level),21);
    const ts=sess.map(s=>new Date(s.date).getTime());
    const _minT=Math.min(...ts),_rng=Math.max(...ts)-_minT||1;
    const _xOf=s=>_pad.l+((new Date(s.date).getTime()-_minT)/_rng)*_iW;
    const _yOf=v=>_pad.t+_iH-((Math.max(0,v))/maxLev)*_iH;
    const rect=svgRef.current.getBoundingClientRect();
    const svgX=(e.clientX-rect.left)*(_W/rect.width);
    let ci=0,cd=Infinity;
    sess.forEach((s,i)=>{const d=Math.abs(_xOf(s)-svgX);if(d<cd){cd=d;ci=i;}});
    setHover({idx:ci,x:_xOf(sess[ci]),y:_yOf(sess[ci].level),level:sess[ci].level,date:sess[ci].date});
  },[trend]);

  const { isDark } = useTheme();
  const T = getT(isDark);

  if(!trend||trend.length===0) return (
    <div style={{ padding:"24px 16px", textAlign:"center", color: T.chartText, fontSize:12,
      fontFamily:T.font, display:"flex", alignItems:"center", justifyContent:"center", gap:6,
      background: T.chartBg, border: T.chartBdr, borderRadius:14, minHeight:150 }}>
      <FaRunning size={18} style={{ opacity:0.15 }}/> No Yo-Yo data
    </div>
  );

  const W=560, H=200;
  const pad={t:16,r:16,b:44,l:44};
  const iW=W-pad.l-pad.r, iH=H-pad.t-pad.b;

  const sessions=trend.map(t=>{
    const vals=["Batting","Bowling","Fielding"].map(c=>t[c]).filter(v=>v!==undefined);
    const avg=vals.length?vals.reduce((a,b)=>a+b,0)/vals.length:0;
    return {date:t.date,level:parseFloat((5+avg*1.6).toFixed(1))};
  });
  const maxLevel=Math.max(...sessions.map(s=>s.level),21);
  const timestamps=sessions.map(s=>new Date(s.date).getTime());
  const minT=Math.min(...timestamps), maxT_ts=Math.max(...timestamps), rng=maxT_ts-minT||1;
  const xOf=s=>pad.l+((new Date(s.date).getTime()-minT)/rng)*iW;
  const yOf=v=>pad.t+iH-((Math.max(0,v))/maxLevel)*iH;
  const barW=Math.max(4,Math.min(14,iW/sessions.length*0.5));

  const fmtDate=ds=>{
    const d=new Date(ds);
    const mn=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${mn[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
  };

  const benchmarks=[
    {level:10,label:"Beginner",color:"rgba(255,90,90,0.65)"},
    {level:14,label:"Intermediate",color:"rgba(245,158,11,0.70)"},
    {level:18,label:"Advanced",color:"rgba(0,230,118,0.70)"},
  ];

  const linePath=()=>{
    const ps=sessions.map(s=>({x:xOf(s),y:yOf(s.level)}));
    if(ps.length<2) return null;
    let d=`M ${ps[0].x.toFixed(1)} ${ps[0].y.toFixed(1)}`;
    for(let i=1;i<ps.length;i++){
      const c1=((ps[i-1].x+ps[i].x)/2).toFixed(1);
      d+=` C ${c1} ${ps[i-1].y.toFixed(1)}, ${c1} ${ps[i].y.toFixed(1)}, ${ps[i].x.toFixed(1)} ${ps[i].y.toFixed(1)}`;
    }
    return d;
  };

  const areaPath=()=>{
    const ps=sessions.map(s=>({x:xOf(s),y:yOf(s.level)}));
    if(ps.length<2) return null;
    const base=pad.t+iH;
    let d=`M ${ps[0].x.toFixed(1)} ${base} L ${ps[0].x.toFixed(1)} ${ps[0].y.toFixed(1)}`;
    for(let i=1;i<ps.length;i++){
      const c1=((ps[i-1].x+ps[i].x)/2).toFixed(1);
      d+=` C ${c1} ${ps[i-1].y.toFixed(1)}, ${c1} ${ps[i].y.toFixed(1)}, ${ps[i].x.toFixed(1)} ${ps[i].y.toFixed(1)}`;
    }
    d+=` L ${ps[ps.length-1].x.toFixed(1)} ${base} Z`;
    return d;
  };

  const lp=linePath(), ap=areaPath();

  return (
    <div style={{ position:"relative", borderRadius:16, overflow:"hidden",
      background: T.chartBg,
      border: T.chartBdr,
      boxShadow: isDark ? "0 4px 32px rgba(0,0,0,0.50), inset 0 1px 0 rgba(255,255,255,0.04)" : "0 4px 32px rgba(3,78,123,0.08), inset 0 1px 0 #fff" }}>
      <svg ref={svgRef} width="100%" viewBox={`0 0 ${W} ${H}`}
        style={{ overflow:"visible", display:"block", cursor:"crosshair" }}
        onMouseMove={handleMouseMove} onMouseLeave={()=>setHover(null)}>
        <defs>
          <linearGradient id="yy-bar-dark" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor="#818cf8" stopOpacity="0.85"/>
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0.15"/>
          </linearGradient>
          <linearGradient id="yy-area-dark" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor="#818cf8" stopOpacity="0.30"/>
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0"/>
          </linearGradient>
          <filter id="yy-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <clipPath id="yy-clip">
            <rect x={pad.l} y={pad.t} width={iW} height={iH}/>
          </clipPath>
        </defs>

        {/* Subtle y-axis grid */}
        {[0,7,14,21].map(v=>(
          <g key={v}>
            <line x1={pad.l} x2={pad.l+iW} y1={yOf(v)} y2={yOf(v)}
              stroke={T.chartGrid} strokeWidth={1}/>
            <text x={pad.l-8} y={yOf(v)+4} textAnchor="end"
              fontSize={11} fill={T.chartText} fontFamily={T.font} fontWeight={600}>{v}</text>
          </g>
        ))}

        {/* Benchmark lines — neon dashed (no right-side labels; see bottom legend) */}
        {benchmarks.map(b=>(
          <line key={b.label} x1={pad.l} x2={pad.l+iW} y1={yOf(b.level)} y2={yOf(b.level)}
            stroke={b.color} strokeWidth={1} strokeDasharray="6,4"/>
        ))}

        {/* Hover crosshair */}
        {hover&&(
          <line x1={hover.x} x2={hover.x} y1={pad.t} y2={pad.t+iH}
            stroke="rgba(255,255,255,0.12)" strokeWidth={1} strokeDasharray="4,3"/>
        )}

        <g clipPath="url(#yy-clip)">
          {/* Area fill */}
          {ap&&<path d={ap} fill="url(#yy-area-dark)"/>}

          {/* Neon bars */}
          {sessions.map((s,i)=>{
            const x=xOf(s),y=yOf(s.level),h=pad.t+iH-y;
            const isHovered=hover&&hover.idx===i;
            return (
              <g key={i}>
                <rect x={x-barW/2} y={y} width={barW} height={h} rx={2.5}
                  fill="url(#yy-bar-dark)" opacity={isHovered?1:0.65}/>
                <rect x={x-barW/2} y={y} width={barW} height={2.5} rx={1.5}
                  fill="#a5b4fc" opacity={isHovered?1:0.80}/>
              </g>
            );
          })}

          {/* Glow layer on line */}
          {lp&&<path d={lp} fill="none" stroke="#818cf8"
            strokeWidth={4} strokeLinejoin="round" strokeLinecap="round" opacity={0.14}/>}

          {/* Trend line — thin & crisp */}
          {lp&&<path d={lp} fill="none" stroke="#818cf8"
            strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round"/>}

          {/* Hover dot */}
          {hover&&(
            <g>
              <circle cx={hover.x} cy={hover.y} r={14} fill="#818cf8" opacity={0.08}/>
              <circle cx={hover.x} cy={hover.y} r={7}  fill="#818cf8" opacity={0.18}/>
              <circle cx={hover.x} cy={hover.y} r={4}  fill="#818cf8" stroke="#fff" strokeWidth={1.5}
                filter="url(#yy-glow)"/>
            </g>
          )}

          {/* Terminal dot */}
          {!hover&&sessions.length>0&&(()=>{
            const last=sessions[sessions.length-1];
            return (
              <g>
                <circle cx={xOf(last)} cy={yOf(last.level)} r={8} fill="#818cf8" opacity={0.14}/>
                <circle cx={xOf(last)} cy={yOf(last.level)} r={3.5} fill="#818cf8" stroke="#fff" strokeWidth={1.5}/>
              </g>
            );
          })()}
        </g>

        {/* X baseline */}
        <line x1={pad.l} x2={pad.l+iW} y1={pad.t+iH} y2={pad.t+iH}
          stroke={T.chartGrid} strokeWidth={1}/>

        {/* X-axis labels */}
        {ticks.map((ds,i)=>{
          const x=pad.l+((new Date(ds).getTime()-minT)/rng)*iW;
          return <text key={i} x={x} y={H-5} textAnchor="middle"
            fontSize={10} fill={T.chartText} fontFamily={T.font} fontWeight={500}>{fmtDate(ds)}</text>;
        })}

        {/* Stats — latest & peak — overlaid top-right inside chart area */}
        {sessions.length>0&&(()=>{
          const latest=sessions[sessions.length-1].level;
          const peak=Math.max(...sessions.map(s=>s.level));
          const sx=pad.l+iW-4; // right-align inside chart
          return (
            <g>
              {/* Semi-transparent backdrop pill */}
              <rect x={sx-74} y={pad.t+4} width={78} height={76} rx={8}
                fill="rgba(4,10,28,0.72)" stroke="rgba(129,140,248,0.18)" strokeWidth={1}/>
              <text x={sx-36} y={pad.t+20} textAnchor="middle" fontSize={8}
                fill="rgba(255,255,255,0.45)" fontFamily={T.font} fontWeight={700} letterSpacing="0.07em">LATEST</text>
              <text x={sx-36} y={pad.t+38} textAnchor="middle" fontSize={18}
                fill="#a5b4fc" fontFamily={T.font} fontWeight={900}>{latest.toFixed(1)}</text>
              <text x={sx-36} y={pad.t+52} textAnchor="middle" fontSize={8}
                fill="rgba(255,255,255,0.45)" fontFamily={T.font} fontWeight={700} letterSpacing="0.07em">PEAK</text>
              <text x={sx-36} y={pad.t+70} textAnchor="middle" fontSize={18}
                fill="#818cf8" fontFamily={T.font} fontWeight={900}>{peak.toFixed(1)}</text>
            </g>
          );
        })()}

        {/* Hover tooltip */}
        {hover&&(()=>{
          const tipW=118, tipH=58;
          let tipX=hover.x+14; if(tipX+tipW>pad.l+iW-80) tipX=hover.x-tipW-14;
          const tipY=pad.t+8;
          return (
            <foreignObject x={tipX} y={tipY} width={tipW} height={tipH} style={{ overflow:"visible" }}>
              <div xmlns="http://www.w3.org/1999/xhtml" style={{
                background:"rgba(4,12,32,0.90)",
                backdropFilter:"blur(16px)",WebkitBackdropFilter:"blur(16px)",
                border:"1px solid rgba(129,140,248,0.25)",
                borderRadius:10, padding:"8px 12px",
                fontFamily:T.font, boxShadow:"0 8px 32px rgba(0,0,0,0.70)",
                width:"100%", boxSizing:"border-box",
              }}>
                <div style={{ fontSize:9,color:"rgba(255,255,255,0.40)",fontWeight:600,marginBottom:6,letterSpacing:"0.07em",textTransform:"uppercase" }}>{fmtDate(hover.date)}</div>
                <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                  <div style={{ width:7,height:7,borderRadius:"50%",background:"#818cf8",boxShadow:"0 0 8px #818cf8" }}/>
                  <span style={{ fontSize:10,color:"rgba(255,255,255,0.55)" }}>Level</span>
                  <span style={{ fontSize:14,fontWeight:900,color:"#a5b4fc",marginLeft:"auto" }}>{hover.level.toFixed(1)}</span>
                </div>
              </div>
            </foreignObject>
          );
        })()}
      </svg>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: DASHBOARD
// ══════════════════════════════════════════════════════════════════════════════
function DashboardTab({ dashboard }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const gc = glassCard(T);
  const globalTf   = useTimeframe();
  const battingTf  = useTimeframe();
  const bowlingTf  = useTimeframe();
  const fieldingTf = useTimeframe();
  const yoyoTf     = useTimeframe();

  const trend_raw       = useMemo(() => dashboard?.trend || EMPTY_ARRAY, [dashboard?.trend]);
  const category_totals = dashboard?.category_totals || [];
  const comments        = dashboard?.comments        || [];
  const student         = dashboard?.student         || {};

  const filteredTrend   = useMemo(()=>filterByTimeframe(trend_raw,globalTf.tfId,globalTf.customFrom,globalTf.customTo),[trend_raw,globalTf.tfId,globalTf.customFrom,globalTf.customTo]);
  const battingFiltered = useMemo(()=>filterByTimeframe(trend_raw,battingTf.tfId,battingTf.customFrom,battingTf.customTo),[trend_raw,battingTf.tfId,battingTf.customFrom,battingTf.customTo]);
  const bowlingFiltered = useMemo(()=>filterByTimeframe(trend_raw,bowlingTf.tfId,bowlingTf.customFrom,bowlingTf.customTo),[trend_raw,bowlingTf.tfId,bowlingTf.customFrom,bowlingTf.customTo]);
  const fieldingFiltered= useMemo(()=>filterByTimeframe(trend_raw,fieldingTf.tfId,fieldingTf.customFrom,fieldingTf.customTo),[trend_raw,fieldingTf.tfId,fieldingTf.customFrom,fieldingTf.customTo]);
  const yoyoFiltered    = useMemo(()=>filterByTimeframe(trend_raw,yoyoTf.tfId,yoyoTf.customFrom,yoyoTf.customTo),[trend_raw,yoyoTf.tfId,yoyoTf.customFrom,yoyoTf.customTo]);

  const radarData=useMemo(()=>["Batting","Bowling","Fielding"].map(cat=>{
    const rows=filteredTrend.filter(t=>t[cat]!==undefined);
    const avg=rows.length?rows.reduce((s,t)=>s+(t[cat]||0),0)/rows.length:0;
    return {label:cat,value:avg};
  }),[filteredTrend]);

  if(!dashboard) return <div style={{ padding:60,textAlign:"center",color:T.gray }}>Loading…</div>;

  const totalKPIs  = category_totals.reduce((s,c)=>s+(c.count||0),0);
  const overallAvg = totalKPIs>0?(category_totals.reduce((s,c)=>s+(parseFloat(c.total)||0),0)/totalKPIs).toFixed(1):"—";
  const age        = student?.date_of_birth?Math.floor((new Date()-new Date(student.date_of_birth))/(365.25*864e5)):null;
  const cats       = ["Batting","Bowling","Fielding"].filter(c=>category_totals.find(ct=>ct.category===c));

  // Stat label style
  const statBlock = (label, value, color) => (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", minWidth:52 }}>
      <div style={{ fontSize:10, fontWeight:700, color: T.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:T.font, marginBottom:2 }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:900, color:color||T.textPrimary, lineHeight:1, fontFamily:T.font }}>{value}</div>
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>

      {/* ══ 1. HERO BAR ══ */}
      <div style={{
        ...gc,
        background: isDark ? "rgba(255,255,255,0.03)" : "rgba(3,78,123,0.02)",
        backdropFilter: "blur(40px)",
        WebkitBackdropFilter: "blur(40px)",
        border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(3,78,123,0.15)",
        boxShadow: T.glassSh,
        overflow: "hidden",
      }}>
        {/* Decorative mesh orbs */}
        <div style={{ position:"relative", overflow:"hidden" }}>
          {/* Unified clean backdrop */}

          <div style={{ display:"flex", alignItems:"center", padding:"14px 20px", gap:0, position:"relative", zIndex:1 }}>

            {/* ── Avatar + name (bigger photo, larger name/PII) ── */}
            <div style={{ display:"flex", alignItems:"center", gap:16, flex:"0 0 auto" }}>
              {/* Bigger avatar */}
              <div style={{ position:"relative", flexShrink:0 }}>
                <div style={{ width:76,height:76,borderRadius:"50%",background: isDark ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.06)",
                  border:`3px solid ${T.yellow}`,display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:22,fontWeight:900,color: isDark ? "#ffffff" : T.blue,overflow:"hidden",
                  boxShadow:`0 0 22px ${T.yellow}50, 0 4px 16px rgba(0,0,0,0.40)` }}>
                  {student?.photo_url
                    ?<img src={`${BASE}/${student.photo_url}`} alt="" style={{ width:"100%",height:"100%",objectFit:"cover" }}/>
                    :`${student?.first_name?.[0]||""}${student?.last_name?.[0]||""}`}
                </div>
                <div style={{ position:"absolute",bottom:3,right:3,width:12,height:12,borderRadius:"50%",
                  background:"#10b981",border:"2px solid #034e7b",boxShadow:"0 0 8px #10b98190" }}/>
              </div>
              {/* Name + PII pills — larger */}
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:20,fontWeight:900,color:T.textPrimary,letterSpacing:"-0.02em",
                  fontFamily:T.font,lineHeight:1.2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>
                  {student?.first_name} {student?.last_name}
                </div>
                <div style={{ display:"flex",gap:8,marginTop:7,alignItems:"center",flexWrap:"wrap" }}>
                  {[
                    {icon:FaCalendarAlt, val:student?.join_date?.slice(0,10)||"—"},
                    {icon:FaVenusMars,   val:age?`${age} yrs`:"—"},
                    {icon:FaMedal,       val:`${totalKPIs} KPIs`},
                  ].map((item,i)=>(
                    <div key={i} style={{ display:"flex",alignItems:"center",gap:4,padding:"3px 10px",
                      borderRadius:20,background:"rgba(255,255,255,0.07)",border:"1px solid rgba(255,255,255,0.12)" }}>
                      <item.icon size={9} style={{ color: isDark ? "rgba(255,255,255,0.50)" : T.textMuted }}/>
                      <span style={{ fontSize:11,fontWeight:600,color: isDark ? "rgba(255,255,255,0.75)" : T.textSub,fontFamily:T.font }}>{item.val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Divider ── */}
            <div style={{ width:1,alignSelf:"stretch",background: isDark ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.12)",flexShrink:0,margin:"0 18px" }}/>

            {/* ── NeonArc overall score gauge ── */}
            <div style={{ flexShrink:0, display:"flex", flexDirection:"column", alignItems:"center" }}>
              <NeonArc value={parseFloat(overallAvg)||0} color={T.yellow} size={120} label="Overall"/>
            </div>

            {/* ── Divider ── */}
            <div style={{ width:1,alignSelf:"stretch",background: isDark ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.12)",flexShrink:0,margin:"0 18px" }}/>

            {/* ── Category score cards — compact, blends with bg ── */}
            <div style={{ display:"flex", gap:8, flex:1, minWidth:0 }}>
              {["Batting","Bowling","Fielding"].map((cat)=>{
                const m   = FIXED_META[cat];
                const ct  = category_totals.find(c=>c.category===cat);
                const avg = ct ? parseFloat(ct.avg).toFixed(1) : "—";
                const pct = ct ? Math.round((parseFloat(ct.avg)/10)*100) : 0;
                return (
                  <div key={cat} style={{
                    flex:1, minWidth:0,
                    padding:"8px 12px",
                    borderRadius:12,
                    /* Glass-blend: very subtle, merges with hero bg */
                    background: isDark ? "rgba(255,255,255,0.07)" : "rgba(3,78,123,0.06)",
                    border:`1px solid ${m.color}35`,
                    backdropFilter:"blur(10px)", WebkitBackdropFilter:"blur(10px)",
                    boxShadow: isDark ? `0 0 18px ${m.color}18` : `0 0 18px ${m.color}08`,
                    transition:"background 0.2s",
                  }}>
                    {/* Icon + label row */}
                    <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:5 }}>
                      <div style={{ width:26,height:26,borderRadius:7,background:`${m.color}20`,
                        border:`1px solid ${m.color}40`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                        <m.icon size={16} style={{ color:m.color }}/>
                      </div>
                      <span style={{ fontSize:9,fontWeight:700,color: isDark ? "rgba(255,255,255,0.55)" : T.textMuted,
                        textTransform:"uppercase",letterSpacing:"0.08em",fontFamily:T.font }}>{cat}</span>
                    </div>
                    {/* Score */}
                    <div style={{ display:"flex",alignItems:"baseline",gap:1,marginBottom:5 }}>
                      <span style={{ fontSize:24,fontWeight:900,color:m.color,lineHeight:1,
                        fontFamily:T.font,textShadow: isDark ? `0 0 12px ${m.color}60` : "none" }}>{avg}</span>
                      <span style={{ fontSize:10,color: isDark ? "rgba(255,255,255,0.40)" : T.textMuted,fontFamily:T.font }}>/10</span>
                    </div>
                    {/* Progress bar */}
                    <div style={{ height:2,borderRadius:1,background:"rgba(255,255,255,0.12)",overflow:"hidden" }}>
                      <div style={{ height:"100%",width:`${pct}%`,borderRadius:1,
                        background:m.grad,boxShadow:`0 0 6px ${m.color}`,transition:"width 0.8s ease" }}/>
                    </div>
                    <div style={{ fontSize:9,color:"rgba(255,255,255,0.35)",fontFamily:T.font,marginTop:3 }}>
                      {ct?.count||0} KPIs · {pct}%</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ══ 3. MINI CATEGORY BAR CHARTS — own timeline each ══ */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
        {[
          {cat:"Batting", tf:battingTf,  filtered:battingFiltered,  id:"bat-bar"},
          {cat:"Bowling", tf:bowlingTf,  filtered:bowlingFiltered,  id:"bow-bar"},
          {cat:"Fielding",tf:fieldingTf, filtered:fieldingFiltered, id:"fld-bar"},
        ].map(({cat,tf,filtered,id})=>{
          const m=FIXED_META[cat];
          const vals=filtered.map(t=>t[cat]).filter(v=>v!==undefined);
          const avg=vals.length?(vals.reduce((a,b)=>a+b,0)/vals.length).toFixed(1):"—";
          const peak=vals.length?Math.max(...vals).toFixed(1):"—";
          const last=vals.length?vals[vals.length-1].toFixed(1):"—";
          return (
            <div key={cat} style={{ 
              ...gc, 
              padding:"14px", 
              background: T.chartBg,
              border: `1.5px solid ${m.border}`, 
              boxShadow: isDark ? `0 4px 18px ${m.color}14, 0 1px 4px rgba(0,0,0,0.40)` : `0 4px 18px ${m.color}08, 0 1px 4px rgba(3,78,123,0.05)`
            }}>
              <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10 }}>
                <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                  <div style={{ width:42,height:42,borderRadius:10,background:m.lightBg,border:`1.5px solid ${m.border}`,display:"flex",alignItems:"center",justifyContent:"center" }}>
                    <m.icon size={24} style={{ color:m.color }}/>
                  </div>
                  <span style={{ fontSize:16,fontWeight:800,color: isDark ? "rgba(255,255,255,0.95)" : "#02304f",fontFamily:T.font }}>{cat}</span>
                </div>
                <TfPill tf={tf}/>
              </div>
              <CategoryBarChart trend={filtered} category={cat} chartId={id}/>
              <div style={{ display:"flex",gap:4,marginTop:12,justifyContent:"space-around",padding:"10px 0 2px",borderTop: `1px solid ${T.chartGrid}` }}>
                {statBlock("Avg",     avg,         m.color)}
                {statBlock("Peak",    peak,        m.color)}
                {statBlock("Latest",  last,        m.color)}
                {vals.length>0 && statBlock("KPIs", vals.length, isDark ? "rgba(255,255,255,0.50)" : "rgba(3,78,123,0.50)")}
              </div>
            </div>
          );
        })}
      </div>

      {/* ══ 4. PERFORMANCE OVER TIME ══ */}
      <div style={{ borderRadius:16,overflow:"hidden",
        background: T.chartBg,
        border: T.chartBdr,
        boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.50), inset 0 1px 0 rgba(255,255,255,0.04)" : "0 8px 32px rgba(3,78,123,0.08), inset 0 1px 0 #fff",
        padding:"16px 20px 14px" }}>
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,gap:12 }}>
          <div style={{ display:"flex",alignItems:"center",gap:10 }}>
            <div style={{ width:3,height:18,borderRadius:2,background:T.yellow,boxShadow:`0 0 10px ${T.yellow}` }}/>
            <span style={{ fontSize:17,fontWeight:800,color: isDark ? "#ffffff" : "#02304f",fontFamily:T.font }}>Performance Over Time</span>
          </div>
          <TfPill tf={globalTf}/>
        </div>
        <TrendChart trend={filteredTrend} categories={cats} chartId="combined"/>
        {filteredTrend.length>0&&(
          <div style={{ display:"flex",gap:10,marginTop:12,justifyContent:"flex-end",flexWrap:"wrap",paddingTop:10,borderTop: `1px solid ${T.chartGrid}` }}>
            {cats.map(cat=>{
              const vals=filteredTrend.map(t=>t[cat]).filter(v=>v!==undefined);
              if(!vals.length) return null;
              const avg=(vals.reduce((a,b)=>a+b,0)/vals.length).toFixed(1);
              const last=vals[vals.length-1]?.toFixed(1);
              const m=FIXED_META[cat]||{color:T.gray};
              return (
                <div key={cat} style={{ fontSize:11,fontFamily:T.font,display:"flex",alignItems:"center",gap:7,padding:"4px 12px",borderRadius:22,
                  background: isDark ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.05)",
                  border: isDark ? `1px solid ${m.color}28` : `1px solid ${m.color}20` }}>
                  <div style={{ width:7,height:7,borderRadius:"50%",background:m.color,boxShadow:`0 0 6px ${m.color}` }}/>
                  <span style={{ fontWeight:800,color:m.color }}>{cat}</span>
                  <span style={{ color: T.chartText }}>avg</span>
                  <strong style={{ color: isDark ? "rgba(255,255,255,0.90)" : "#02304f" }}>{avg}</strong>
                  <span style={{ color: T.chartGrid }}>·</span>
                  <strong style={{ color:m.color }}>{last}</strong>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ══ 5. YO-YO + TRIANGLE — side by side, equal size ══ */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, alignItems:"stretch" }}>

        {/* Yo-Yo test */}
        <div style={{ borderRadius:16,overflow:"hidden",
          background: T.chartBg,
          border: T.chartBdr,
          boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.50), inset 0 1px 0 rgba(255,255,255,0.04)" : "0 8px 32px rgba(3,78,123,0.08), inset 0 1px 0 #fff",
          padding:"16px 20px 14px" }}>
          <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,gap:12 }}>
            <div style={{ display:"flex",alignItems:"center",gap:10 }}>
              <div style={{ width:36,height:36,borderRadius:10,background:"rgba(129,140,248,0.12)",border:"1px solid rgba(129,140,248,0.25)",display:"flex",alignItems:"center",justifyContent:"center" }}>
                <FaRunning size={20} style={{ color:"#a5b4fc" }}/>
              </div>
              <span style={{ fontSize:17,fontWeight:800,color: isDark ? "#ffffff" : "#02304f",fontFamily:T.font }}>Yo-Yo Endurance Test</span>
            </div>
            <TfPill tf={yoyoTf}/>
          </div>
          <YoYoChart trend={yoyoFiltered}/>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",
            marginTop:12,paddingTop:10,borderTop: `1px solid ${T.chartGrid}` }}>
            {[
              {label:"Beginner",    color:"rgba(255,90,90,0.95)",  range:"≤10"},
              {label:"Intermediate",color:"rgba(245,158,11,1.00)", range:"11–17"},
              {label:"Advanced",    color:"rgba(0,230,118,1.00)",  range:"18+"},
            ].map(b=>(
              <div key={b.label} style={{ display:"flex",alignItems:"center",gap:8 }}>
                <div style={{ width:16,height:3,borderRadius:2,background:b.color,
                  boxShadow:`0 0 8px ${b.color}` }}/>
                <span style={{ fontSize:12,fontFamily:T.font,color: isDark ? "rgba(255,255,255,0.60)" : "rgba(3,78,123,0.65)",fontWeight:600 }}>{b.label}</span>
                <span style={{ fontSize:12,fontFamily:T.font,color:b.color,fontWeight:800 }}>{b.range}</span>
              </div>
            ))}
          </div>
        </div>

        {/* KPI Triangle */}
        <div style={{ borderRadius:16,overflow:"hidden",
          background: T.chartBg,
          border: T.chartBdr,
          boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.50), inset 0 1px 0 rgba(255,255,255,0.04)" : "0 8px 32px rgba(3,78,123,0.08), inset 0 1px 0 #fff",
          padding:"16px 20px 16px" }}>
          <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,gap:12 }}>
            <div style={{ display:"flex",alignItems:"center",gap:10 }}>
              <div style={{ width:3,height:18,borderRadius:2,background:T.yellow,boxShadow:`0 0 10px ${T.yellow}` }}/>
              <span style={{ fontSize:17,fontWeight:800,color: isDark ? "#ffffff" : "#02304f",fontFamily:T.font }}>KPI Triangle</span>
            </div>
            <TfPill tf={globalTf}/>
          </div>
          <div style={{ display:"flex",alignItems:"center",justifyContent:"center",padding:"0" }}>
            <TriangleRadar data={radarData} size={340}/>
          </div>
        </div>
      </div>

      {/* ══ 6. COACH COMMENTS ══ */}
      <div style={{ ...gc, padding:"14px 16px 16px" }}>
        <SectionLabel>Coach Comments</SectionLabel>
        {comments.length>0?(
          <div style={{ display:"flex",flexDirection:"column",gap:7 }}>
            {comments.map((c,i)=>(
              <div key={i} style={{ padding:"12px 18px",borderRadius:12,
                background: isDark ? (i%2===0 ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.02)") : (i%2===0 ? "rgba(3,78,123,0.04)" : "rgba(3,78,123,0.02)"),
                border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(3,78,123,0.08)",
                boxShadow: isDark ? "inset 0 1px 0 rgba(255,255,255,0.04)" : "inset 0 1px 0 #fff" }}>
                <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:8 }}>
                  <div style={{ width:24,height:24,borderRadius:"50%",background:`linear-gradient(135deg,${T.yellow}cc,${T.yellow}66)`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:`0 0 10px ${T.yellow}40` }}>
                    <FaCommentDots size={12} color="#0a1832"/>
                  </div>
                  <span style={{ fontSize:10,fontWeight:800,color: isDark ? "rgba(255,255,255,0.38)" : "rgba(3,78,123,0.45)",fontFamily:T.font,letterSpacing:"0.08em",textTransform:"uppercase" }}>{c.date}</span>
                </div>
                <p style={{ margin:0,fontSize:13,color: isDark ? "rgba(255,255,255,0.85)" : "#02304f",lineHeight:1.65,fontFamily:T.font, fontWeight:500 }}>{c.text}</p>
              </div>
            ))}
          </div>
        ):(
          <div style={{ color:T.gray,fontSize:12,fontFamily:T.font,padding:"6px 0" }}>No coach comments recorded yet.</div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: METRICS — Compact square cards, glass effect, proper sizing
// ══════════════════════════════════════════════════════════════════════════════
function MetricsTab({ dashboard }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  if(!dashboard) return null;
  const category_totals = dashboard.category_totals||[];
  const latest_scores   = dashboard.latest_scores  ||[];
  const trend_raw       = dashboard.trend           ||[];

  const totalKPIs  = category_totals.reduce((s,c)=>s+(c.count||0),0);
  const overallAvg = totalKPIs>0?(category_totals.reduce((s,c)=>s+(parseFloat(c.total)||0),0)/totalKPIs).toFixed(1):"—";
  const totalPts   = category_totals.reduce((s,c)=>s+(parseFloat(c.total)||0),0).toFixed(0);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

      {/* ── Summary banner ── */}
      <div style={{
        borderRadius:14, padding:"14px 20px",
        background:"linear-gradient(135deg, #02304f 0%, #034e7b 45%, #045a8d 75%, #0369a1 100%)",
        boxShadow:"0 6px 28px rgba(2,48,79,0.26), 0 1px 4px rgba(0,40,100,0.10)",
        border:"1px solid rgba(255,255,255,0.12)",
        display:"flex", alignItems:"center", gap:18, flexWrap:"wrap",
      }}>
        {/* NeonArc gauge */}
        <div style={{ flexShrink:0 }}>
          <NeonArc value={parseFloat(overallAvg)||0} color={T.yellow} size={120} label="Overall Avg"/>
        </div>

        <div style={{ flex:1, minWidth:200 }}>
          <div style={{ fontSize:20, fontWeight:900, color:"white", fontFamily:T.font, marginBottom:8 }}>Performance Summary</div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {category_totals.map((ct,idx)=>{
              const m=getCatMeta(ct.category,idx);
              return (
                <div key={ct.category} style={{ display:"flex", alignItems:"center", gap:7, padding:"6px 14px", borderRadius:20, background:"rgba(255,255,255,0.09)", border:`1px solid ${m.color}38` }}>
                  <m.icon size={30} style={{ color:m.color }}/>
                  <span style={{ fontSize:12, color:"rgba(255,255,255,0.70)", fontFamily:T.font }}>{ct.category}</span>
                  <span style={{ fontSize:17, fontWeight:900, color:m.color, fontFamily:T.font }}>{parseFloat(ct.avg).toFixed(1)}</span>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize:10, color:"rgba(255,255,255,0.40)", fontFamily:T.font, marginTop:8 }}>
            {totalKPIs} KPIs · {category_totals.length} categories · {totalPts} total pts
          </div>
        </div>
      </div>

      {/* ── COMPACT SQUARE CATEGORY CARDS ── */}
      <div>
        <SectionLabel>Performance Categories</SectionLabel>
        {/* Responsive: up to 4 per row, each card is squarish */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(200px, 1fr))", gap:12 }}>
          {category_totals.map((ct,idx)=>{
            const m    = getCatMeta(ct.category, idx);
            const Icon = m.icon;
            const avg  = parseFloat(ct.avg)||0;
            const pct  = Math.round((avg/10)*100);
            const tot  = parseFloat(ct.total)||0;
            return (
              <div key={ct.category} style={{
                background:"rgba(255,255,255,0.04)",
                backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)",
                border:`1.5px solid ${m.border}`,
                borderRadius:16,
                boxShadow:`0 4px 24px rgba(0,0,0,0.50), 0 0 20px ${m.color}10`,
                overflow:"hidden",
                position:"relative",
                transition:"transform 0.18s, box-shadow 0.18s",
              }}
                onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow=`0 12px 36px rgba(0,0,0,0.60), 0 0 30px ${m.color}25`;}}
                onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow=`0 4px 24px rgba(0,0,0,0.50), 0 0 20px ${m.color}10`;}}
              >
                {/* Glowing top accent */}
                <div style={{ height:3, background:m.grad, width:"100%", boxShadow:`0 0 10px ${m.color}` }}/>

                <div style={{ padding:"12px 14px 14px" }}>
                  {/* Header */}
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0, flex:1 }}>
                      <div style={{ width:30, height:30, borderRadius:8, background:m.lightBg, border:`1px solid ${m.border}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        <Icon size={14} style={{ color:m.color }}/>
                      </div>
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:800, color:T.textPrimary, fontFamily:T.font, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{ct.category}</div>
                        <div style={{ fontSize:10, color:T.textMuted, fontFamily:T.font }}>{ct.count} KPIs</div>
                      </div>
                    </div>
                    <div style={{ padding:"3px 9px", borderRadius:14, background:m.lightBg, border:`1px solid ${m.border}`, flexShrink:0 }}>
                      <span style={{ fontSize:18, fontWeight:900, color:m.color, fontFamily:T.font, lineHeight:1, textShadow:`0 0 12px ${m.color}70` }}>{avg.toFixed(1)}</span>
                      <span style={{ fontSize:10, color:"rgba(255,255,255,0.30)", fontFamily:T.font }}>/10</span>
                    </div>
                  </div>

                  {/* Glowing progress bar */}
                  <div style={{ height:4, borderRadius:2, background:"rgba(255,255,255,0.07)", overflow:"hidden", marginBottom:10 }}>
                    <div style={{ height:"100%", width:`${pct}%`, borderRadius:2, background:m.grad, boxShadow:`0 0 8px ${m.color}`, transition:"width 0.8s ease" }}/>
                  </div>

                  <div style={{ marginBottom:8 }}>
                    <MiniSparkline trend={trend_raw} catKey={ct.category}/>
                  </div>

                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                    {[
                      {label:"Total Pts", val:tot.toFixed(0)},
                      {label:"Progress",  val:`${pct}%`},
                    ].map(stat=>(
                      <div key={stat.label} style={{
                        padding:"6px 8px", borderRadius:8,
                        background: isDark ? "rgba(255,255,255,0.05)" : "rgba(3,78,123,0.04)",
                        border:`1px solid ${m.border}`,
                      }}>
                        <div style={{ fontSize:11, fontWeight:700, color:T.textMuted, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:T.font }}>{stat.label}</div>
                        <div style={{ fontSize:16, fontWeight:900, color:m.color, fontFamily:T.font, lineHeight:1.3, textShadow: isDark ? `0 0 8px ${m.color}50` : "none" }}>{stat.val}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Per-indicator breakdowns ── */}
      {category_totals.map((ct,idx)=>{
        const inds=latest_scores.filter(s=>s.category_name===ct.category);
        if(!inds.length) return null;
        const m=getCatMeta(ct.category,idx);
        return (
          <div key={ct.category}>
            <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 16px", borderRadius:12, marginBottom:10, background: isDark?"rgba(255,255,255,0.04)":"rgba(3,78,123,0.04)", border:`1px solid ${m.border}`, boxShadow: isDark?`0 4px 20px rgba(0,0,0,0.40), 0 0 20px ${m.color}08`:`0 4px 15px ${m.color}15`, position:"relative", overflow:"hidden" }}>
              <div style={{ position:"absolute", left:0, top:0, bottom:0, width:3, background:m.grad, boxShadow:`0 0 8px ${m.color}`, borderRadius:"12px 0 0 12px" }}/>
              <div style={{ width:28, height:28, borderRadius:8, marginLeft:6, flexShrink:0, background:m.lightBg, border:`1px solid ${m.border}`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <m.icon size={12} style={{ color:m.color }}/>
              </div>
              <span style={{ fontWeight:800, color:T.textPrimary, fontSize:16, flex:1, fontFamily:T.font }}>{ct.category}</span>
              <span style={{ padding:"2px 10px", borderRadius:16, background:m.lightBg, border:`1px solid ${m.border}`, fontSize:9, fontWeight:700, color:m.color, fontFamily:T.font }}>{inds.length} KPIs</span>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))", gap:8 }}>
              {inds.map(ind=>{
                const sc  = ind.score||0;
                const col = sc>=8?T.green:sc>=5?T.orange:T.red;
                const bdr = sc>=8?T.green:sc>=5?T.orange:T.red;
                const bg  = sc>=8?T.greenBg:sc>=5?T.orangeBg:T.redBg;
                return (
                  <div key={ind.indicator_id} style={{ background:bg, border:`1px solid ${bdr}44`, borderRadius:10, padding:"12px 14px", display:"flex", flexDirection:"column", gap:7 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:T.textSub, fontFamily:T.font, lineHeight:1.3 }}>{ind.indicator_name}</div>
                    <div style={{ height:4, borderRadius:2, background:"rgba(255,255,255,0.08)", overflow:"hidden" }}>
                      <div style={{ height:"100%", borderRadius:2, width:`${sc*10}%`, background:`linear-gradient(90deg,${col},${col}bb)`, boxShadow:`0 0 6px ${col}`, transition:"width 0.8s ease" }}/>
                    </div>
                    <div style={{ display:"flex", alignItems:"baseline", gap:3 }}>
                      <span style={{ fontSize:22, fontWeight:900, color:col, fontFamily:T.font, lineHeight:1, textShadow: isDark ? `0 0 10px ${col}60` : "none" }}>{sc}</span>
                      <span style={{ fontSize:11, color:T.textMuted }}>/10</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Standalone editable field ────────────────────────────────────────────────
function EditField({ label, name, type="text", icon:Icon, value, onChange }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  return (
    <div>
      <label style={lbl(T)}>{label}</label>
      <div style={iw(T)}>
        {Icon&&<Icon size={20} style={{ marginRight:12,color:T.textMuted,flexShrink:0 }}/>}
        <input type={type} name={name} value={value??""} onChange={onChange} autoComplete="off" style={is(T)}/>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: MY CHILD
// ══════════════════════════════════════════════════════════════════════════════
function MyChildTab({ currentUser, dashboard, onSaved }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const gc = glassCard(T);
  const [form,   setForm]   = useState(null);
  const [msg,    setMsg]    = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(()=>{
    if(dashboard?.student){
      const s=dashboard.student;
      setForm({ first_name:s.first_name||"", last_name:s.last_name||"", date_of_birth:s.date_of_birth?.slice(0,10)||"", gender:s.gender||"", parent_name:s.parent_name||"", parent_email:s.parent_email||"", parent_phone:s.parent_phone||"" });
    }
  },[dashboard]);

  if(!form) return null;

  const ch=e=>{const{name,value}=e.target;setForm(prev=>({...prev,[name]:value}));};

  const handleSave=async()=>{
    setSaving(true);setMsg(null);
    try{
      const fd=new FormData();
      Object.entries(form).forEach(([k,v])=>fd.append(k,v||""));
      fd.append("updated_by",String(currentUser.parent_id||"parent"));
      const r=await fetch(`${BASE}/students/${dashboard.student.student_id}`,{method:"PUT",body:fd});
      const d=await r.json();
      setMsg({ok:d.success,text:d.success?"Contact details saved!":(d.message||"Error saving")});
      if(d.success) onSaved&&onSaved();
    }catch{setMsg({ok:false,text:"Network error — please try again."});}
    finally{setSaving(false);}
  };

  const student  = dashboard?.student || {};
  const totals   = dashboard?.category_totals || [];
  const totalKPIs= totals.reduce((s,c)=>s+(c.count||0),0);
  const overallAvg= totalKPIs>0?(totals.reduce((s,c)=>s+(parseFloat(c.total)||0),0)/totalKPIs).toFixed(1):"—";
  const age      = student?.date_of_birth?Math.floor((new Date()-new Date(student.date_of_birth))/(365.25*864e5)):null;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:0 }}>

      {/* ── Hero bar — full width, matches DashboardTab ── */}
      <div style={{
        ...gc,
        background: isDark ? "rgba(255,255,255,0.03)" : "rgba(3,78,123,0.02)",
        backdropFilter: "blur(40px)",
        WebkitBackdropFilter: "blur(40px)",
        border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(3,78,123,0.15)",
        boxShadow: T.glassSh,
        overflow: "hidden",
        marginBottom:24,
      }}>
        {/* Decorative orbs */}
        <div style={{ position:"relative" }}>
          {/* Unified clean backdrop */}

          <div style={{ display:"flex", alignItems:"center", padding:"14px 20px", gap:0, position:"relative", zIndex:1 }}>

            {/* ── Avatar + name (matches DashboardTab) ── */}
            <div style={{ display:"flex", alignItems:"center", gap:16, flex:"0 0 auto" }}>
              <div style={{ position:"relative", flexShrink:0 }}>
                <div style={{ width:76,height:76,borderRadius:"50%",background: isDark ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.06)",
                  border:`3px solid ${T.yellow}`,display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:22,fontWeight:900,color: isDark ? "#ffffff" : T.blue,overflow:"hidden",
                  boxShadow:`0 0 22px ${T.yellow}50, 0 4px 16px rgba(0,0,0,0.40)` }}>
                  {student?.photo_url?<img src={`${BASE}/${student.photo_url}`} alt="" style={{ width:"100%",height:"100%",objectFit:"cover" }}/>:`${student?.first_name?.[0]||""}${student?.last_name?.[0]||""}`}
                </div>
                <div style={{ position:"absolute",bottom:3,right:3,width:12,height:12,borderRadius:"50%",
                  background:"#10b981",border:"2px solid #034e7b",boxShadow:"0 0 8px #10b98190" }}/>
              </div>
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:20,fontWeight:900,color:T.textPrimary,letterSpacing:"-0.02em",
                  fontFamily:T.font,lineHeight:1.2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>
                  {student?.first_name} {student?.last_name}
                </div>
                <div style={{ display:"flex",gap:8,marginTop:7,alignItems:"center",flexWrap:"wrap" }}>
                  {[
                    {icon:FaCalendarAlt, val:student?.join_date?.slice(0,10)||"—"},
                    {icon:FaVenusMars,   val:age?`${age} yrs`:"—"},
                    {icon:FaMedal,       val:`${totalKPIs} KPIs`},
                  ].map((item,i)=>(
                    <div key={i} style={{ display:"flex",alignItems:"center",gap:4,padding:"3px 10px",
                      borderRadius:20,background:"rgba(255,255,255,0.07)",border:"1px solid rgba(255,255,255,0.12)" }}>
                      <item.icon size={9} style={{ color: isDark ? "rgba(255,255,255,0.50)" : T.textMuted }}/>
                      <span style={{ fontSize:11,fontWeight:600,color: isDark ? "rgba(255,255,255,0.75)" : T.textSub,fontFamily:T.font }}>{item.val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div style={{ width:1,alignSelf:"stretch",background:"rgba(255,255,255,0.08)",flexShrink:0,margin:"0 18px" }}/>

            {/* NeonArc */}
            <div style={{ flexShrink:0, display:"flex", flexDirection:"column", alignItems:"center" }}>
              <NeonArc value={parseFloat(overallAvg)||0} color={T.yellow} size={120} label="Overall"/>
            </div>

            {/* Divider */}
            <div style={{ width:1,alignSelf:"stretch",background:"rgba(255,255,255,0.08)",flexShrink:0,margin:"0 18px" }}/>

            {/* Category score cards — glass-blend matching DashboardTab */}
            <div style={{ display:"flex", gap:8, flex:1, minWidth:0 }}>
              {["Batting","Bowling","Fielding"].map(cat=>{
                const m  = FIXED_META[cat];
                const ct = totals.find(c=>c.category===cat);
                const avg= ct?parseFloat(ct.avg).toFixed(1):"—";
                const pct= ct?Math.round((parseFloat(ct.avg)/10)*100):0;
                return (
                  <div key={cat} style={{
                    flex:1, minWidth:0,
                    padding:"8px 12px",
                    borderRadius:12,
                    background:"rgba(255,255,255,0.07)",
                    border:`1px solid ${m.color}35`,
                    backdropFilter:"blur(10px)", WebkitBackdropFilter:"blur(10px)",
                    boxShadow:`0 0 18px ${m.color}18`,
                    transition:"background 0.2s",
                  }}>
                    <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:5 }}>
                      <div style={{ width:26,height:26,borderRadius:7,background:`${m.color}20`,
                        border:`1px solid ${m.color}40`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                        <m.icon size={16} style={{ color:m.color }}/>
                      </div>
                      <span style={{ fontSize:9,fontWeight:700,color:T.textLabel,
                        textTransform:"uppercase",letterSpacing:"0.08em",fontFamily:T.font }}>{cat}</span>
                    </div>
                    <div style={{ display:"flex",alignItems:"baseline",gap:1,marginBottom:5 }}>
                      <span style={{ fontSize:24,fontWeight:900,color:m.color,lineHeight:1,
                        fontFamily:T.font,textShadow: isDark ? `0 0 12px ${m.color}60` : "none" }}>{avg}</span>
                      <span style={{ fontSize:10,color:T.textMuted,fontFamily:T.font }}>/10</span>
                    </div>
                    <div style={{ height:2,borderRadius:1,background:"rgba(255,255,255,0.12)",overflow:"hidden" }}>
                      <div style={{ height:"100%",width:`${pct}%`,borderRadius:1,
                        background:m.grad,boxShadow:`0 0 6px ${m.color}`,transition:"width 0.8s ease" }}/>
                    </div>
                    <div style={{ fontSize:9,color:T.textMuted,fontFamily:T.font,marginTop:3 }}>
                      {ct?.count||0} KPIs · {pct}%</div>
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      </div>

      {/* ── Centered form content ── */}
      <div style={{ maxWidth:680, margin:"0 auto", width:"100%", padding:"0 4px" }}>

        {msg&&(
          <div style={{ display:"flex",alignItems:"center",gap:9,padding:"11px 16px",borderRadius:10,marginBottom:16,fontSize:12,fontWeight:600,fontFamily:T.font,
            background:msg.ok?"rgba(0,230,118,0.10)":"rgba(255,77,77,0.10)",
            color:msg.ok?"#00e676":"#ff4d4d",
            border:msg.ok?"1px solid rgba(0,230,118,0.30)":"1px solid rgba(255,77,77,0.30)" }}>
            {msg.ok?<FaCheckCircle size={14}/>:<FaExclamationCircle size={14}/>} {msg.text}
          </div>
        )}

        {/* Student info — read only */}
        <div style={{ ...gc, padding:"20px 24px", marginBottom:14 }}>
          <SectionLabel>Student Information (Read Only)</SectionLabel>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"14px 24px" }}>
            {[
              {label:"First Name",     val:form.first_name,    icon:FaUserCircle},
              {label:"Last Name",      val:form.last_name,     icon:FaUserCircle},
              {label:"Date of Birth",  val:form.date_of_birth, icon:FaCalendarAlt},
              {label:"Gender",         val:form.gender,        icon:FaVenusMars},
            ].map(({label,val,icon:IcoField})=>(
              <div key={label}>
                <label style={lbl(T)}>{label}</label>
                <div style={{ ...iw(T), background:T.rowOdd, border:`1.5px solid ${T.divider}`, cursor:"not-allowed" }}>
                  <IcoField size={20} style={{ marginRight:12, color:T.textMuted, flexShrink:0 }}/>
                  <span style={{ ...is(T), color:T.textMuted, userSelect:"none" }}>{val||"—"}</span>
                </div>
                <p style={{ fontSize:10,color:"rgba(255,255,255,0.30)",margin:"3px 0 0",fontFamily:T.font }}>Contact administrator to change.</p>
              </div>
            ))}
          </div>
        </div>

        {/* Parent contact — editable */}
        <div style={{ ...gc, padding:"20px 24px", marginBottom:20 }}>
          <SectionLabel>Parent / Guardian Contact Details</SectionLabel>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"14px 24px" }}>
            <EditField label="Parent / Guardian Name" name="parent_name"  icon={FaUserCircle} value={form.parent_name}  onChange={ch}/>
            <EditField label="Parent Email"           name="parent_email" type="email" icon={FaEnvelope} value={form.parent_email} onChange={ch}/>
            <EditField label="Parent Phone"           name="parent_phone" icon={FaPhone}    value={form.parent_phone} onChange={ch}/>
          </div>
        </div>

        {/* Save button — centered */}
        <div style={{ display:"flex", justifyContent:"center" }}>
          <button onClick={handleSave} disabled={saving}
            style={{ padding:"12px 48px",border:"none",borderRadius:12,fontSize:13,fontWeight:700,
              cursor:saving?"not-allowed":"pointer",color:"#080c14",fontFamily:T.font,
              display:"flex",alignItems:"center",gap:8,
              backgroundImage:`linear-gradient(135deg,${T.yellow},#ff9500)`,
              boxShadow:`0 4px 20px rgba(247,206,11,0.35)`,
              opacity:saving?0.70:1,transition:"all 0.18s" }}>
            <FaSave size={14}/> {saving?"Saving…":"Save Changes"}
          </button>
        </div>

      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ROOT — ParentDashboard  (supports multiple linked students / siblings)
// ══════════════════════════════════════════════════════════════════════════════
export default function ParentDashboard({ currentUser, onLogout }) {
  const { config } = useAppConfig();
  const { isDark, toggleTheme } = useTheme();
  const T = getT(isDark);

  const logoSrc   = config.logo_url        || SixersLogo;
  const orgName   = config.org_name        || "Sixers Sports Center";
  const primary   = config.primary_color   || T.blue;
  const secondary = config.secondary_color || T.yellow;
  const fontFam   = `'${config.font_family||"Segoe UI"}', system-ui, sans-serif`;

  const hexToRgb = h => {
    try { const b=parseInt((h||"#034e7b").replace("#",""),16); return `${(b>>16)&255},${(b>>8)&255},${b&255}`; }
    catch { return "3,78,123"; }
  };
  const primaryRGB   = hexToRgb(primary);
  const secondaryRGB = hexToRgb(secondary);

  // ── linked students from login payload ─────────────────────────────────────
  const linkedStudents = Array.isArray(currentUser?.linked_students) && currentUser.linked_students.length
    ? currentUser.linked_students
    : [{ student_id: currentUser?.student_id, first_name: currentUser?.student_first_name, last_name: currentUser?.student_last_name, photo_url: currentUser?.student_photo_url, student_code: currentUser?.student_code, is_primary: 1 }];

  const [activeStudentId, setActiveStudentId] = useState(linkedStudents[0]?.student_id);
  const [tab,             setTab]             = useState("dashboard");
  const [dashboard,       setDashboard]       = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [canEval,         setCanEval]         = useState(false);

  const fetchAuth = async () => {
    try {
      const r = await fetch(`${BASE}/field-auth/`);
      const d = await r.json();
      if (d.matrix) {
        const access = d.matrix["Coach Evaluation"]?.["submit_evaluation"]?.["Parent"] || "none";
        setCanEval(access !== "none");
      }
    } catch (e) {
      // Default to true if fetch fails or for initial development if needed, 
      // but user asked for "selectable" so we should respect the matrix.
      console.error("Auth fetch failed", e);
    }
  };

  useEffect(() => { fetchAuth(); }, []);

  const activeStudent = linkedStudents.find(s => s.student_id === activeStudentId) || linkedStudents[0];
  const studentName   = activeStudent ? `${activeStudent.first_name} ${activeStudent.last_name}` : "your child";

  const loadDashboard = async (sid) => {
    if (!sid) return;
    setLoading(true); setDashboard(null);
    try {
      const r = await fetch(`${BASE}/performance/dashboard/${sid}`);
      const d = await r.json();
      setDashboard(d);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { loadDashboard(activeStudentId); }, [activeStudentId]);

  const switchStudent = (sid) => {
    setActiveStudentId(sid);
    setTab("dashboard");
  };

  const perms = currentUser?.permissions || [];
  
  // Dynamic Tab check based on RoleMaster selections
  const showTab = (lbl) => (perms.length === 0 || perms.includes(lbl));

  const TABS = [];
  if (showTab("Performance Dashboard")) TABS.push({ id:"dashboard", label:"Performance Dashboard", icon:FaChartLine });
  if (showTab("Metrics"))               TABS.push({ id:"metrics",   label:"Metrics",               icon:FaTrophy    });
  if (showTab("My Child"))              TABS.push({ id:"child",     label:"My Child",              icon:FaChild     });
  
  // Evaluation: Either it's explicitly in 'permissions' or it's turned on in 'field-auth' 
  // (we'll keep both as a transition, but focus on permissions if present)
  if (showTab("Coach Evaluation") && (perms.includes("Coach Evaluation") || canEval)) {
    TABS.push({ id:"evaluation", label:"Coach Evaluation", icon:FaStar });
  }

  // Ensure 'tab' points to a valid tab if the current one was hidden
  useEffect(() => {
    if (TABS.length > 0 && !TABS.find(t => t.id === tab)) {
      setTab(TABS[0].id);
    }
  }, [TABS.length]);

  return (
    <div style={{ display:"flex", height:"100vh", position:"relative", overflow:"hidden", minHeight:"-webkit-fill-available", fontFamily:fontFam }}>
      <style>{`
        @keyframes pd_haloBlue   { 0%,100%{box-shadow:0 0 0 4px rgba(${primaryRGB},0.08),0 0 14px 4px rgba(${primaryRGB},0.18)} 50%{box-shadow:0 0 0 7px rgba(${primaryRGB},0.13),0 0 28px 8px rgba(${primaryRGB},0.34)} }
        @keyframes pd_haloYellow { 0%,100%{box-shadow:0 0 0 4px rgba(${secondaryRGB},0.12),0 0 14px 4px rgba(${secondaryRGB},0.28)} 50%{box-shadow:0 0 0 7px rgba(${secondaryRGB},0.20),0 0 28px 8px rgba(${secondaryRGB},0.52)} }
      `}</style>
      <FuturisticBackground />

      {/* ── Sidebar — Floating Futuristic Glass ── */}
      <div style={{
        width:200, flexShrink:0,
        margin: "14px 0 14px 14px",
        borderRadius: 24,
        position:"relative", overflow:"hidden",
        display:"flex", flexDirection:"column",
        padding:"14px 10px",
        boxShadow: isDark ? "0 20px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)" : "0 20px 50px rgba(3,78,123,0.15), 0 0 0 1px rgba(255,255,255,0.6)",
        zIndex: 100
      }}>
        {/* Sidebar Background (Image + Glass) */}
        <div style={{
          position: "absolute", inset:0, zIndex:-1,
          backgroundImage: `url(${bg6})`, backgroundSize: "cover", backgroundPosition: "center",
          opacity: isDark ? 0.12 : 0.06
        }} />
        <div style={{
          position: "absolute", inset:0, zIndex:-1,
          background: isDark ? "rgba(2,13,24,0.78)" : "rgba(255,255,255,0.78)",
          backdropFilter:"blur(32px)", WebkitBackdropFilter:"blur(32px)"
        }} />

        {/* Theme toggle — top-left icon */}
        <div onClick={toggleTheme} title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"} style={{
          position:"absolute", top:12, left:10, zIndex:20,
          width:28, height:28, borderRadius:8,
          background: isDark ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.07)",
          border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(3,78,123,0.14)",
          display:"flex", alignItems:"center", justifyContent:"center",
          cursor:"pointer", transition:"all 0.2s",
        }}
          onMouseEnter={e=>{ e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.12)" : "rgba(3,78,123,0.13)"; }}
          onMouseLeave={e=>{ e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.07)"; }}>
          {isDark ? <FaSun size={13} color={secondary}/> : <FaMoon size={13} color={primary}/>}
        </div>

        <div style={{ flex:1, minHeight:0, overflowY:"auto", overflowX:"hidden" }}>


          {/* Logo */}
          <div style={{ textAlign:"center", padding:"4px 0 6px", marginBottom:8, borderBottom:"1px solid rgba(255,255,255,0.08)" }}>
            {/* Logo ring with animated halo */}
            <div style={{
              width:64, height:64, borderRadius:"50%",
              border: isDark ? `2.5px solid rgba(${secondaryRGB},0.70)` : `2.5px solid rgba(${primaryRGB},0.55)`,
              display:"flex", alignItems:"center", justifyContent:"center",
              margin:"0 auto",
              background: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.82)",
              animation: isDark ? "pd_haloYellow 2.6s ease-in-out infinite" : "pd_haloBlue 2.6s ease-in-out infinite",
            }}>
              <img src={logoSrc} alt={orgName} style={{ width:50, objectFit:"contain", opacity:0.95 }}/>
            </div>
          </div>

          {/* Parent name card */}
          <div style={{ 
            padding:"9px 11px", borderRadius:12, marginBottom:10, 
            background: isDark ? "rgba(255,255,255,0.05)" : "rgba(3,78,123,0.08)", 
            border: isDark ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(3,78,123,0.15)", 
            boxShadow:"inset 0 1px 0 rgba(255,255,255,0.05)" 
          }}>
            <div style={{ fontSize:8, fontWeight:800, color:secondary, textTransform:"uppercase", letterSpacing:"0.09em", fontFamily:fontFam, marginBottom:2 }}>Logged in as</div>
            <div style={{ 
              fontSize:12, fontWeight:700, 
              color: isDark ? "rgba(255,255,255,0.88)" : "#02304f", 
              fontFamily:fontFam, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" 
            }}>{currentUser?.full_name||"Parent"}</div>
          </div>

          {/* Student switcher */}
          {linkedStudents.length > 1 && (
            <div style={{ marginBottom:10 }}>
              <div style={{ fontSize:8, fontWeight:800, color: isDark ? "rgba(255,255,255,0.30)" : "rgba(3,78,123,0.45)", textTransform:"uppercase", letterSpacing:"0.09em", padding:"0 4px", marginBottom:6, fontFamily:fontFam }}>Switch Student</div>
              <div style={{ maxHeight: 180, overflowY: "auto", paddingRight: 4, marginRight: -4 }}>
                {linkedStudents.map(s => {
                  const isActive = s.student_id === activeStudentId;
                  const initials = `${(s.first_name||"?")[0]}${(s.last_name||"?")[0]}`.toUpperCase();
                  const baseItemBg = isDark ? "rgba(255,255,255,0.04)" : "rgba(3,78,123,0.05)";
                  const baseItemBdr = isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(3,78,123,0.10)";
                  const baseText = isDark ? "rgba(255,255,255,0.55)" : "rgba(3,78,123,0.60)";
                  const activeText = isDark ? "rgba(255,255,255,0.95)" : "#02304f";

                  return (
                    <div key={s.student_id} onClick={() => switchStudent(s.student_id)}
                      style={{ cursor:"pointer", WebkitTapHighlightColor:"transparent", display:"flex", alignItems:"center", gap:8, padding:"7px 9px", borderRadius:10, marginBottom:3,
                        background: isActive ? `rgba(247,206,11,0.12)` : baseItemBg,
                        border: isActive ? `1px solid ${secondary}40` : baseItemBdr,
                        boxShadow: isActive ? `0 0 14px ${secondary}20` : "none",
                        transition:"all 0.18s" }}>
                      <div style={{ width:28, height:28, borderRadius:"50%", flexShrink:0, overflow:"hidden",
                        background: isActive ? `${secondary}33` : (isDark ? "rgba(255,255,255,0.10)" : "rgba(3,78,123,0.10)"),
                        border: isActive ? `1.5px solid ${secondary}` : (isDark ? "1.5px solid rgba(255,255,255,0.12)" : "1.5px solid rgba(3,78,123,0.12)"),
                        display:"flex", alignItems:"center", justifyContent:"center",
                        boxShadow: isActive ? `0 0 8px ${secondary}60` : "none" }}>
                        {s.photo_url
                          ? <img src={`${BASE}/${s.photo_url}`} alt={initials} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                          : <span style={{ fontSize:9, fontWeight:800, color: isActive ? secondary : (isDark ? "rgba(255,255,255,0.65)" : "rgba(3,78,123,0.60)"), fontFamily:fontFam }}>{initials}</span>
                        }
                      </div>
                      <div style={{ minWidth:0, flex:1 }}>
                        <div style={{ fontSize:11, fontWeight: isActive ? 700 : 500, color: isActive ? activeText : baseText, fontFamily:fontFam, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {s.first_name} {s.last_name}
                        </div>
                        {s.student_code && <div style={{ fontSize:9, color: isDark ? "rgba(255,255,255,0.28)" : "rgba(3,78,123,0.40)", fontFamily:fontFam }}>{s.student_code}</div>}
                      </div>
                      {isActive && <div style={{ width:6, height:6, borderRadius:"50%", background:secondary, flexShrink:0, boxShadow:`0 0 6px ${secondary}` }}/>}
                    </div>
                  );
                })}
              </div>
              <div style={{ height:1, background: isDark ? "rgba(255,255,255,0.07)" : "rgba(3,78,123,0.08)", margin:"10px 4px" }}/>
            </div>
          )}

          {/* Single student indicator */}
          {linkedStudents.length === 1 && (
            <div style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 10px", borderRadius:10, marginBottom:10, background: isDark ? "rgba(255,255,255,0.04)" : "rgba(3,78,123,0.05)", border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(3,78,123,0.10)" }}>
              <FaChild size={14} style={{ color:secondary, flexShrink:0, filter:`drop-shadow(0 0 4px ${secondary})` }}/>
              <span style={{ fontSize:10, color: isDark ? "rgba(255,255,255,0.55)" : "rgba(3,78,123,0.60)", fontFamily:fontFam, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{studentName}</span>
            </div>
          )}

          {/* Divider */}
          <div style={{ height:1, background: isDark ? "rgba(255,255,255,0.07)" : "rgba(3,78,123,0.08)", margin:"4px 0 10px" }}/>

          {/* Nav tabs */}
          {TABS.map(t => {
            const Icon = t.icon, on = tab === t.id;
            const baseIcon = isDark ? "rgba(255,255,255,0.35)" : "rgba(3,78,123,0.40)";
            const baseText = isDark ? "rgba(255,255,255,0.48)" : "rgba(3,78,123,0.65)";
            const activeText = isDark ? "rgba(255,255,255,0.95)" : "#02304f";
            
            return (
              <div key={t.id} onClick={() => setTab(t.id)}
                style={{ cursor:"pointer", WebkitTapHighlightColor:"transparent", display:"flex", alignItems:"center", gap:8, padding:"9px 11px", borderRadius:10, marginBottom:3,
                  background: on ? `rgba(247,206,11,0.12)` : (isDark ? "rgba(255,255,255,0.04)" : "rgba(3,78,123,0.05)"),
                  border: on ? `1px solid ${secondary}40` : (isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(3,78,123,0.10)"),
                  boxShadow: on ? `0 0 16px ${secondary}15` : "none",
                  color: on ? activeText : baseText,
                  fontSize:12, fontFamily:fontFam, fontWeight: on ? 700 : 400, transition:"all 0.18s",
                  position:"relative", overflow:"hidden" }}>
                {/* Left accent bar when active */}
                {on && <div style={{ position:"absolute",left:0,top:0,bottom:0,width:3,background:`linear-gradient(180deg,${secondary},#ff9500)`,borderRadius:"10px 0 0 10px",boxShadow:`0 0 8px ${secondary}` }}/>}
                <Icon size={16} style={{ color: on ? secondary : baseIcon, flexShrink:0, filter: on ? `drop-shadow(0 0 4px ${secondary})` : "none" }}/>
                <span style={{ lineHeight:1.3 }}>{t.label}</span>
              </div>
            );
          })}
        </div>

        <div style={{ flexShrink:0, paddingTop:6 }}>
          {/* Sign out */}
          <div onClick={onLogout}
            style={{ cursor:"pointer", WebkitTapHighlightColor:"transparent", display:"flex", alignItems:"center", gap:9, padding:"8px 10px", borderRadius:9,
              background:"transparent",
              border:"1px solid transparent",
              color: isDark ? "rgba(255,255,255,0.52)" : "rgba(3,78,123,0.60)",
              fontSize:13, fontWeight:600, fontFamily:fontFam, transition:"all 0.14s" }}
            onMouseEnter={e=>{
              e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.10)" : "rgba(3,78,123,0.06)";
              e.currentTarget.style.border = isDark ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(3,78,123,0.18)";
              e.currentTarget.style.color = isDark ? "rgba(255,255,255,0.90)" : "#02304f";
            }}
            onMouseLeave={e=>{
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.border = "1px solid transparent";
              e.currentTarget.style.color = isDark ? "rgba(255,255,255,0.52)" : "rgba(3,78,123,0.60)";
            }}>
            <FaSignOutAlt size={18} style={{ flexShrink:0 }}/> Sign Out
          </div>
        </div>
      </div>

      {/* ── Content area ── */}
      <div style={{ flex:1, overflowY:"auto", WebkitOverflowScrolling:"touch", display:"flex", flexDirection:"column" }}>
        <div style={{ padding:"14px 18px 36px", flex:1 }}>
          {loading
            ? <div style={{ textAlign:"center", padding:80, color:"rgba(255,255,255,0.30)", fontFamily:fontFam, fontSize:13 }}>Loading dashboard…</div>
            : <>
                {tab==="dashboard" && <DashboardTab dashboard={dashboard}/>}
                {tab==="metrics"   && <MetricsTab   dashboard={dashboard}/>}
                {tab==="child"     && <MyChildTab   currentUser={{ ...currentUser, student_id: activeStudentId, student_first_name: activeStudent?.first_name, student_last_name: activeStudent?.last_name }} dashboard={dashboard} onSaved={()=>loadDashboard(activeStudentId)}/>}
                {tab==="evaluation" && <CoachEvaluation />}
              </>
          }
        </div>
      </div>
    </div>
  );
}
